const BASE = process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000';

function getToken() { return localStorage.getItem('br_token'); }

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        if (res.status === 401) {
            if (token) {
                // Session expired — clear token and redirect to login
                localStorage.removeItem('br_token');
                window.location.href = '/';
                return undefined as T;
            }
            // Login failure — throw so the form can show the error
            throw new Error(err.error || 'Invalid email or password.');
        }
        throw new Error(err.error || res.statusText);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

export const api = {
    // Auth
    loginCustomer: (email: string, password: string) =>
        req<LoginResponse>('POST', '/api/auth/login', { email, password }),
    loginContractor: (email: string, password: string) =>
        req<LoginResponse>('POST', '/api/auth/login', { email, password }),
    loginAdmin: (email: string, password: string) =>
        req<LoginResponse>('POST', '/api/auth/login', { email, password }),
    registerCustomer: (data: RegisterCustomerDto) =>
        req<AuthResponse>('POST', '/api/auth/register/customer', data),
    registerContractor: (data: RegisterContractorDto) =>
        req<AuthResponse>('POST', '/api/auth/register/contractor', data),
    forgotPassword: (email: string) =>
        req<{ message: string }>('POST', '/api/auth/forgot-password', { email }),
    resetPassword: (token: string, email: string, newPassword: string) =>
        req<{ message: string }>('POST', '/api/auth/reset-password', { token, email, newPassword }),
    verifyMfa: (pendingToken: string, otp: string) =>
        req<AuthResponse>('POST', '/api/auth/verify-mfa', { pendingToken, otp }),
    enableMfa: (enabled: boolean) =>
        req<{ mfaEnabled: boolean }>('POST', '/api/auth/mfa/enable', { enabled }),
    getMfaStatus: () =>
        req<{ mfaEnabled: boolean }>('GET', '/api/auth/mfa/status'),

    // Jobs
    getQuote: (serviceType: string, estimatedUnits: number, address?: { pickupCity?: string; pickupState?: string; pickupCounty?: string; pickupPostalCode?: string; pickupCountry?: string }, shredding?: { shreddingRequestType?: string; shreddingSizeId?: string; shreddingCustomerType?: string }) =>
        req<QuoteResponse>('POST', '/api/jobs/quote', { serviceType, estimatedUnits, ...address, ...shredding }),
    createJob: (data: CreateJobDto) => req<Job>('POST', '/api/jobs', data),

    // Gift vouchers — purchase (customer), redeem-at-checkout validate (any role), admin list/void.
    purchaseGiftVoucher: (dto: { serviceType: string; recipientName: string; recipientEmail: string; message?: string; shreddingSizeId?: string; shreddingCustomerType?: string; shreddingRequestType?: string; shreddingEstimatedUnits?: number }) =>
        req<GiftVoucherPurchaseResponse>('POST', '/api/gift-vouchers/purchase', dto),
    confirmGiftVoucherPurchase: (paymentIntentId: string) =>
        req<void>('POST', `/api/gift-vouchers/confirm/${encodeURIComponent(paymentIntentId)}`),
    validateGiftVoucher: (code: string, serviceType: string, shreddingSizeId?: string, shreddingRequestType?: string, shreddingEstimatedUnits?: number) =>
        req<ValidateGiftVoucherResponse>('POST', '/api/gift-vouchers/validate', { code, serviceType, shreddingSizeId, shreddingRequestType, shreddingEstimatedUnits }),
    getGiftVouchersAdmin: () => req<GiftVoucherDto[]>('GET', '/api/gift-vouchers'),
    voidGiftVoucher: (id: string) => req<void>('POST', `/api/gift-vouchers/${id}/void`),
    getJob: (id: string) => req<Job>('GET', `/api/jobs/${id}`),
    getMyJobs: () => req<Job[]>('GET', '/api/jobs/mine'),
    getNearbyJobs: (lat: number, lng: number, radius: number = 50) =>
        req<ContractorJob[]>('GET', `/api/jobs/nearby?lat=${lat}&lng=${lng}&radius=${radius}`),
    getContractorJobs: () => req<ContractorJob[]>('GET', '/api/jobs/contractor/mine'),
    acceptJob: (id: string) => req<ContractorJob>('POST', `/api/jobs/${id}/accept`),
    updateStatus: (id: string, status: string) =>
        req<ContractorJob>('PUT', `/api/jobs/${id}/status`, { status }),
    updateActualUnits: (jobId: string, actualUnits: number) =>
        req<ContractorJob>('PUT', `/api/jobs/${jobId}/actual-units`, { actualUnits }),
    requestEarlyStart: (jobId: string) =>
        req<ContractorJob>('POST', `/api/jobs/${jobId}/early-start/request`),
    respondToEarlyStart: (jobId: string, approve: boolean) =>
        req<Job>('POST', `/api/jobs/${jobId}/early-start/respond`, { approve }),

    // Location
    sendLocation: (jobId: string, lat: number, lng: number) =>
        req<void>('POST', '/api/location', { jobId, lat, lng, speedKph: 30, headingDegrees: 180 }),

    // Ratings
    submitRating: (jobId: string, stars: number, comment?: string) =>
        req<Rating>('POST', '/api/ratings', { jobId, stars, comment }),

    // Payments
    createPaymentIntent: (jobId: string) =>
        req<PaymentIntentResponse>('POST', `/api/payments/${jobId}/intent`),
    confirmJobAuthorization: (jobId: string) =>
        req<void>('POST', `/api/payments/${jobId}/authorized`, {}),
    confirmDumpsterAuthorization: (orderId: string) =>
        req<void>('POST', `/api/payments/dumpster/${orderId}/authorized`, {}),

    // Service types
    getServiceTypes: () =>
        req<{ value: string; label: string; unitLabel: string; isFlatRate: boolean; allowPhotoUpload: boolean; requiresDonationCenter: boolean; estimateWholePile: boolean; requiresDocumentCategories: boolean; allowGifting: boolean }[]>('GET', '/api/jobs/service-types'),

    // Donation centers (Donation Run service types only — see PricingRule.RequiresDonationCenter)
    getDonationCenters: () =>
        req<DonationCenterDto[]>('GET', '/api/jobs/donation-centers'),

    // Cancellation
    getCancellationPolicy: (jobId: string) =>
        req<CancellationPolicyResponse>('GET', `/api/jobs/${jobId}/cancellation-policy`),
    cancelJob: (jobId: string, reason: string) =>
        req<CancellationResponse>('POST', `/api/jobs/${jobId}/cancel`, { reason }),

    // Price adjustment
    getPriceAdjustment: (jobId: string) =>
        req<PriceAdjustmentResponse>('GET', `/api/price-adjustment/${jobId}`),
    requestPriceAdjustment: (jobId: string, actualUnits: number, notes?: string) =>
        req<PriceAdjustmentResponse>('POST', `/api/price-adjustment/${jobId}/request`, { actualUnits, notes }),
    completeLineItemJob: (jobId: string) =>
        req<PriceAdjustmentResponse>('POST', `/api/price-adjustment/${jobId}/complete-catalog`),
    respondToPriceAdjustment: (jobId: string, approved: boolean, disputeReason?: string) =>
        req<PriceAdjustmentResponse>('POST', `/api/price-adjustment/${jobId}/respond`, { approved, disputeReason }),
    adminApprovePriceAdjustment: (jobId: string) =>
        req<PriceAdjustmentResponse>('POST', `/api/price-adjustment/${jobId}/admin-approve`),
    adminDeclinePriceAdjustment: (jobId: string) =>
        req<PriceAdjustmentResponse>('POST', `/api/price-adjustment/${jobId}/admin-decline`),

    // Admin
    getStats: () => req<DashboardStats>('GET', '/api/admin/stats'),
    getAllJobs: () => req<JobListItem[]>('GET', '/api/admin/jobs'),
    getJobsPaged: (page: number, pageSize: number, status?: string, contractorName?: string) => {
        const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (status && status !== 'All') params.set('status', status);
        if (contractorName && contractorName !== 'All') params.set('contractorName', contractorName);
        return req<PagedJobsResponse>('GET', `/api/admin/jobs/paged?${params.toString()}`);
    },
    getContractors: () => req<ContractorListItem[]>('GET', '/api/admin/contractors'),
    getPricing: () => req<PricingRule[]>('GET', '/api/admin/pricing'),
    updatePricing: (serviceType: string, basePrice: number, pricePerUnit: number) =>
        req<PricingRule>('PUT', '/api/admin/pricing', { serviceType, basePrice, pricePerUnit }),
    addPricingRule: (serviceTypeName: string, basePrice: number, pricePerUnit: number, unitLabel: string, description?: string, allowPhotoUpload?: boolean, requiredSkills?: string[], preorderEnabled?: boolean, launchDate?: string | null, requiresDonationCenter?: boolean, estimateWholePile?: boolean, requiresDocumentCategories?: boolean, shreddingRetrievalFee?: number, allowGifting?: boolean) =>
        req<PricingRule>('POST', '/api/admin/pricing', { serviceTypeName, basePrice, pricePerUnit, unitLabel, description, allowPhotoUpload: allowPhotoUpload ?? false, requiredSkills, preorderEnabled: preorderEnabled ?? false, launchDate, requiresDonationCenter: requiresDonationCenter ?? false, estimateWholePile: estimateWholePile ?? false, requiresDocumentCategories: requiresDocumentCategories ?? false, shreddingRetrievalFee: shreddingRetrievalFee ?? 0, allowGifting: allowGifting ?? true }),
    updatePricingById: (id: string, basePrice: number, pricePerUnit: number, unitLabel: string, isActive: boolean, description?: string, serviceTypeName?: string, allowPhotoUpload?: boolean, requiredSkills?: string[], preorderEnabled?: boolean, launchDate?: string | null, requiresDonationCenter?: boolean, estimateWholePile?: boolean, requiresDocumentCategories?: boolean, shreddingRetrievalFee?: number, allowGifting?: boolean) =>
        req<PricingRule>('PUT', `/api/admin/pricing/${id}`, { basePrice, pricePerUnit, unitLabel, isActive, description, serviceTypeName, allowPhotoUpload: allowPhotoUpload ?? false, requiredSkills, preorderEnabled: preorderEnabled ?? false, launchDate, requiresDonationCenter: requiresDonationCenter ?? false, estimateWholePile: estimateWholePile ?? false, requiresDocumentCategories: requiresDocumentCategories ?? false, shreddingRetrievalFee: shreddingRetrievalFee ?? 0, allowGifting: allowGifting ?? true }),
    deletePricingRule: (id: string) =>
        req<void>('DELETE', `/api/admin/pricing/${id}`),

    // Donation Centers (admin)
    getDonationCentersAdmin: () => req<DonationCenterDto[]>('GET', '/api/admin/donation-centers'),
    addDonationCenter: (name: string, address: string, geo: DonationCenterGeo, phone?: string, taxId?: string, sortOrder?: number, description?: string) =>
        req<DonationCenterDto>('POST', '/api/admin/donation-centers', { name, address, ...geo, phone, taxId, sortOrder: sortOrder ?? 0, description }),
    updateDonationCenter: (id: string, name: string, address: string, geo: DonationCenterGeo, phone?: string, taxId?: string, isActive?: boolean, sortOrder?: number, description?: string) =>
        req<DonationCenterDto>('PUT', `/api/admin/donation-centers/${id}`, { name, address, ...geo, phone, taxId, isActive: isActive ?? true, sortOrder: sortOrder ?? 0, description }),
    deleteDonationCenter: (id: string) =>
        req<void>('DELETE', `/api/admin/donation-centers/${id}`),

    // Document Shredding — bins (admin)
    getShreddingBins: () => req<ShreddingBinDto[]>('GET', '/api/admin/shredding-bins'),
    createShreddingBin: (label: string, sizeId: string) =>
        req<ShreddingBinDto>('POST', '/api/admin/shredding-bins', { label, sizeId }),
    getShreddingBinScans: (id: string) =>
        req<BinScanEventDto[]>('GET', `/api/admin/shredding-bins/${id}/scans`),
    updateShreddingBinStatus: (id: string, status: string) =>
        req<ShreddingBinDto>('PUT', `/api/admin/shredding-bins/${id}/status`, { status }),
    // PNG is behind [Authorize], so a plain <img src> can't carry the bearer token — fetch as a
    // blob and hand back an object URL instead.
    getShreddingBinQrCodeUrl: async (id: string): Promise<string> => {
        const token = getToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${BASE}/api/admin/shredding-bins/${id}/qr-code`, { headers });
        if (!res.ok) throw new Error('Failed to load QR code.');
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    },

    // Document Shredding — bin size catalog (admin management; full list incl. inactive)
    getShreddingBinSizesAdmin: () => req<ShreddingBinSizeDto[]>('GET', '/api/admin/shredding-bins/sizes'),
    createShreddingBinSize: (dto: { name: string; price: number; availableToResidential: boolean; availableToBusiness: boolean }) =>
        req<ShreddingBinSizeDto>('POST', '/api/admin/shredding-bins/sizes', dto),
    updateShreddingBinSize: (id: string, dto: { name: string; price: number; availableToResidential: boolean; availableToBusiness: boolean; isActive: boolean }) =>
        req<ShreddingBinSizeDto>('PUT', `/api/admin/shredding-bins/sizes/${id}`, dto),
    // Bins currently out in the field, for the admin Live Map's bin layer.
    getShreddingBinLocations: () => req<ShreddingBinLocationDto[]>('GET', '/api/admin/shredding-bins/locations'),

    // Document Shredding — customer actions
    // Gates whether the booking flow offers "service my bin" / "pick up my bin" — only valid
    // once a delivery has actually happened (JobService.CreateJobAsync enforces this too).
    getMyShreddingBinStatus: () => req<CustomerBinStatusDto>('GET', '/api/shredding/my-bin-status'),
    // Active (non-retired) sizes only — what the booking flow's size picker shows.
    getShreddingBinSizes: () => req<ShreddingBinSizeDto[]>('GET', '/api/shredding/sizes'),

    // Document Shredding — contractor actions
    getShreddingJobCategories: (jobId: string) =>
        req<string[]>('GET', `/api/shredding/jobs/${jobId}/categories`),
    // Bin inventory for the web manual-entry fallback's typeahead (contractor-reachable, unlike
    // getShreddingBins above which is the Admin-only /api/admin/shredding-bins route).
    getContractorShreddingBins: () => req<ShreddingBinDto[]>('GET', '/api/shredding/bins'),
    // Scan history for the tracker UI — which checkpoints are already done for this bin.
    getContractorShreddingBinScans: (binId: string) =>
        req<BinScanEventDto[]>('GET', `/api/shredding/bins/${binId}/scans`),
    scanShreddingBin: (binId: string, scanType: string, jobId?: string, lat?: number, lng?: number, notes?: string) =>
        req<ShreddingBinDto>('POST', `/api/shredding/bins/${binId}/scan`, { scanType, jobId, lat, lng, notes }),
    scanShreddingBinByLabel: (label: string, scanType: string, jobId?: string, lat?: number, lng?: number, notes?: string) =>
        req<ShreddingBinDto>('POST', `/api/shredding/bins/scan-by-label`, { label, scanType, jobId, lat, lng, notes }),
    confirmShreddingCategories: (jobId: string) =>
        req<void>('POST', `/api/shredding/jobs/${jobId}/confirm-categories`),

    // Contractor skills & certifications (TREK-75)
    addContractorSkill: (contractorId: string, name: string) =>
        req<ContractorSkillDto>('POST', `/api/admin/contractors/${contractorId}/skills`, { name }),
    removeContractorSkill: (contractorId: string, skillId: string) =>
        req<void>('DELETE', `/api/admin/contractors/${contractorId}/skills/${skillId}`),
    getMyContractorSkills: () => req<ContractorSkillDto[]>('GET', '/api/contractor/skills').catch(() => []),

    // Supply & inventory tracking (TREK-81)
    getInventory: () => req<InventoryItemDto[]>('GET', '/api/admin/inventory'),
    addInventoryItem: (name: string, category: string | null, quantityOnHand: number, reorderThreshold: number, unitCost: number) =>
        req<InventoryItemDto>('POST', '/api/admin/inventory', { name, category, quantityOnHand, reorderThreshold, unitCost }),
    updateInventoryItem: (id: string, name: string, category: string | null, reorderThreshold: number, unitCost: number) =>
        req<InventoryItemDto>('PUT', `/api/admin/inventory/${id}`, { name, category, reorderThreshold, unitCost }),
    adjustInventory: (id: string, delta: number, reason: string) =>
        req<InventoryItemDto>('POST', `/api/admin/inventory/${id}/adjust`, { delta, reason }),
    getInventoryHistory: (id: string) => req<InventoryUsageDto[]>('GET', `/api/admin/inventory/${id}/history`),
    downloadInventoryCsv: async () => {
        const token = getToken();
        const res = await fetch(`${BASE}/api/admin/inventory/export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error('Could not export inventory.');
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    },
    getJobInventoryUsage: (jobId: string) => req<InventoryUsageDto[]>('GET', `/api/jobs/${jobId}/inventory-usage`).catch(() => []),
    logJobInventoryUsage: (jobId: string, inventoryItemId: string, quantity: number) =>
        req<InventoryUsageDto>('POST', `/api/jobs/${jobId}/inventory-usage`, { inventoryItemId, quantity }),

    // Property Records (core primitive)
    getProperties: () => req<PropertyDto[]>('GET', '/api/admin/properties'),
    getProperty: (id: string) => req<PropertyDto>('GET', `/api/admin/properties/${id}`),
    createProperty: (data: CreatePropertyRequest) => req<PropertyDto>('POST', '/api/admin/properties', data),
    updateProperty: (id: string, data: UpdatePropertyRequest) => req<PropertyDto>('PUT', `/api/admin/properties/${id}`, data),
    getPropertyHistory: (id: string) => req<PropertyHistoryItemDto[]>('GET', `/api/admin/properties/${id}/history`),

    // Compliance Documents (core primitive)
    getComplianceDocumentsForHolder: (holderType: string, holderId?: string) =>
        req<ComplianceDocumentDto[]>('GET', `/api/admin/compliance-documents?holderType=${encodeURIComponent(holderType)}${holderId ? `&holderId=${encodeURIComponent(holderId)}` : ''}`),
    getExpiringComplianceDocuments: (withinDays = 30) =>
        req<ComplianceDocumentDto[]>('GET', `/api/admin/compliance-documents/expiring-soon?withinDays=${withinDays}`),
    createComplianceDocument: (data: CreateComplianceDocumentRequest) =>
        req<ComplianceDocumentDto>('POST', '/api/admin/compliance-documents', data),
    updateComplianceDocumentStatus: (id: string, status: string, rejectionReason?: string | null) =>
        req<ComplianceDocumentDto>('PUT', `/api/admin/compliance-documents/${id}/status`, { status, rejectionReason }),

    // Photo Checklists (core primitive)
    getPhotoChecklistTemplates: () => req<PhotoChecklistTemplateDto[]>('GET', '/api/admin/photo-checklists/templates'),
    createPhotoChecklistTemplate: (data: SavePhotoChecklistTemplateRequest) =>
        req<PhotoChecklistTemplateDto>('POST', '/api/admin/photo-checklists/templates', data),
    updatePhotoChecklistTemplate: (id: string, data: SavePhotoChecklistTemplateRequest) =>
        req<PhotoChecklistTemplateDto>('PUT', `/api/admin/photo-checklists/templates/${id}`, data),
    assignPhotoChecklistToJob: (jobId: string, templateId: string | null) =>
        req<void>('POST', `/api/admin/photo-checklists/jobs/${jobId}/assign${templateId ? `/${templateId}` : ''}`),
    getJobPhotoChecklistStatus: (jobId: string) =>
        req<JobPhotoChecklistStatusDto>('GET', `/api/jobs/${jobId}/photo-checklist`),
    submitJobPhoto: (jobId: string, data: SubmitJobPhotoRequest) =>
        req<JobPhotoSubmissionDto>('POST', `/api/jobs/${jobId}/photo-checklist`, data),

    // Job SLA / Due-Date Tracking (core primitive)
    getAtRiskJobSlas: (withinHours = 24) => req<JobSlaDto[]>('GET', `/api/admin/job-sla/at-risk?withinHours=${withinHours}`),
    getJobSla: (jobId: string) => req<JobSlaDto | null>('GET', `/api/admin/job-sla/${jobId}`).catch(() => null),
    setJobSla: (jobId: string, dueDate: string, priority: string) =>
        req<JobSlaDto>('PUT', `/api/admin/job-sla/${jobId}`, { dueDate, priority }),
    escalateJobSla: (jobId: string) => req<JobSlaDto>('POST', `/api/admin/job-sla/${jobId}/escalate`),

    // Client Portal — Admin-side management (core infrastructure)
    getClients: () => req<ClientUserDto[]>('GET', '/api/admin/clients'),
    registerClient: (data: RegisterClientRequest) => req<ClientUserDto>('POST', '/api/admin/clients', data),
    getClientPropertyGrants: (clientUserId: string) => req<ClientPropertyAccessDto[]>('GET', `/api/admin/clients/${clientUserId}/properties`),
    grantClientPropertyAccess: (clientUserId: string, propertyId: string) =>
        req<void>('POST', `/api/admin/clients/${clientUserId}/properties/${propertyId}`),
    revokeClientPropertyAccess: (clientUserId: string, propertyId: string) =>
        req<void>('DELETE', `/api/admin/clients/${clientUserId}/properties/${propertyId}`),

    // Client Portal — self-service (Client role)
    getMyClientProperties: () => req<ClientPortalPropertyDto[]>('GET', '/api/client/properties'),
    getMyClientProperty: (propertyId: string) => req<ClientPortalPropertyDto>('GET', `/api/client/properties/${propertyId}`),
    getMyClientPropertyHistory: (propertyId: string) => req<PropertyHistoryItemDto[]>('GET', `/api/client/properties/${propertyId}/history`),

    // Digital work orders & signature (TREK-88)
    getWorkOrder: (jobId: string) => req<WorkOrderDto | null>('GET', `/api/jobs/${jobId}/work-order`).catch(() => null),

    // Service catalog (TREK-132)
    getServiceCatalog: () => req<ServiceCatalogItemDto[]>('GET', '/api/service-catalog'),
    getServiceCatalogAll: () => req<ServiceCatalogItemDto[]>('GET', '/api/service-catalog/all'),
    addServiceCatalogItem: (name: string, category: string, description: string | null, pricingType: ServiceCatalogPricingType, price: number, unit: string | null) =>
        req<ServiceCatalogItemDto>('POST', '/api/service-catalog', { name, category, description, pricingType, price, unit }),
    updateServiceCatalogItem: (id: string, name: string, category: string, description: string | null, price: number, unit: string | null) =>
        req<ServiceCatalogItemDto>('PUT', `/api/service-catalog/${id}`, { name, category, description, price, unit }),
    deactivateServiceCatalogItem: (id: string) =>
        req<ServiceCatalogItemDto>('POST', `/api/service-catalog/${id}/deactivate`),
    activateServiceCatalogItem: (id: string) =>
        req<ServiceCatalogItemDto>('POST', `/api/service-catalog/${id}/activate`),
    getLaborRate: () => req<PricingConfigDto>('GET', '/api/service-catalog/labor-rate'),
    updateLaborRate: (standardLaborRate: number, afterHoursMultiplier: number) =>
        req<PricingConfigDto>('PUT', '/api/service-catalog/labor-rate', { standardLaborRate, afterHoursMultiplier }),

    // Configurable job fee + sales tax (general BoxRunner tenants)
    getServiceFeeConfig: () => req<ServiceFeeConfigDto>('GET', '/api/admin/service-fee'),
    updateServiceFeeConfig: (config: ServiceFeeConfigDto) =>
        req<ServiceFeeConfigDto>('PUT', '/api/admin/service-fee', config),

    // Estimate Builder — prospect-first, typed line items, branded PDF
    getEstimateBuilderDocs: () => req<EstimateBuilderDocDto[]>('GET', '/api/estimates'),
    getEstimateBuilderDoc: (id: string) => req<EstimateBuilderDocDto>('GET', `/api/estimates/${id}`),
    searchProspects: (search: string) =>
        req<ProspectDto[]>('GET', `/api/estimates/prospects?search=${encodeURIComponent(search)}`),
    createEstimateBuilderDoc: (body: CreateEstimateBuilderRequest) =>
        req<EstimateBuilderDocDto>('POST', '/api/estimates', body),
    updateEstimateBuilderDoc: (id: string, body: UpdateEstimateBuilderRequest) =>
        req<EstimateBuilderDocDto>('PUT', `/api/estimates/${id}`, body),
    deleteEstimateBuilderDoc: (id: string) => req<void>('DELETE', `/api/estimates/${id}`),
    addEstimateLineItem: (estimateId: string, body: AddEstimateLineItemRequest) =>
        req<EstimateLineItemDto>('POST', `/api/estimates/${estimateId}/line-items`, body),
    updateEstimateLineItem: (estimateId: string, lineItemId: string, body: UpdateEstimateLineItemRequest) =>
        req<EstimateLineItemDto>('PUT', `/api/estimates/${estimateId}/line-items/${lineItemId}`, body),
    setEstimateLineItemSelected: (estimateId: string, lineItemId: string, selected: boolean) =>
        req<EstimateLineItemDto>('POST', `/api/estimates/${estimateId}/line-items/${lineItemId}/select`, { selected }),
    removeEstimateLineItem: (estimateId: string, lineItemId: string) =>
        req<void>('DELETE', `/api/estimates/${estimateId}/line-items/${lineItemId}`),
    sendEstimateBuilderDoc: (id: string) => req<SendEstimateResponse>('POST', `/api/estimates/${id}/send`),
    approveEstimateBuilderDoc: (id: string, signature?: { signatureBase64Png?: string | null; lat?: number | null; lng?: number | null; signedByName?: string | null }) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimates/${id}/approve`, signature),
    declineEstimateBuilderDoc: (id: string, note?: string) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimates/${id}/decline`, { note }),
    getEstimateBuilderPdf: (id: string) => req<{ pdfUrl: string }>('GET', `/api/estimates/${id}/pdf`),
    convertEstimateBuilderDocToJob: (id: string) => req<EstimateBuilderDocDto>('POST', `/api/estimates/${id}/convert-to-job`),
    createEstimateCustomer: (id: string) => req<EstimateBuilderDocDto>('POST', `/api/estimates/${id}/create-customer`),
    getMyAssignedEstimates: () => req<EstimateBuilderDocDto[]>('GET', '/api/estimates/mine'),
    assignEstimateToContractor: (id: string, contractorId: string) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimates/${id}/assign`, { contractorId }),

    // Customer/prospect "Request an Estimate" — logged-in customer vs. anonymous prospect
    requestEstimate: (body: RequestEstimateRequest) => req<EstimateBuilderDocDto>('POST', '/api/estimate-requests', body),
    requestEstimatePublic: (body: RequestEstimateRequest) => req<EstimateBuilderDocDto>('POST', '/api/estimate-requests/public', body),
    getMyEstimatesAsCustomer: () => req<EstimateBuilderDocDto[]>('GET', '/api/estimate-requests/mine'),
    approveMyEstimate: (id: string, signature?: { signatureBase64Png?: string | null; lat?: number | null; lng?: number | null; signedByName?: string | null }) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimate-requests/${id}/approve`, signature),
    declineMyEstimate: (id: string, note?: string) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimate-requests/${id}/decline`, { note }),
    createMyEstimatePaymentIntent: (id: string) =>
        req<PaymentIntentResponse>('POST', `/api/estimate-requests/${id}/payment-intent`),
    confirmMyEstimatePayment: (id: string) =>
        req<EstimateBuilderDocDto>('POST', `/api/estimate-requests/${id}/payment-confirmed`),

    // Public, tokenized estimate access — no login, used by the /estimate/:token page.
    getPublicEstimate: (token: string) => req<PublicEstimateDto>('GET', `/api/public/estimates/${token}`),
    setPublicEstimateLineItemSelected: (token: string, lineItemId: string, selected: boolean) =>
        req<PublicEstimateDto>('POST', `/api/public/estimates/${token}/line-items/${lineItemId}/select`, { selected }),
    approvePublicEstimate: (token: string, body: { signatureBase64Png?: string | null; lat?: number | null; lng?: number | null; signedByName?: string | null }) =>
        req<PublicEstimateDto>('POST', `/api/public/estimates/${token}/approve`, body),
    declinePublicEstimate: (token: string, note?: string) =>
        req<PublicEstimateDto>('POST', `/api/public/estimates/${token}/decline`, { note }),
    createPublicEstimatePaymentIntent: (token: string) =>
        req<PaymentIntentResponse>('POST', `/api/public/estimates/${token}/payment-intent`),
    confirmPublicEstimatePayment: (token: string) =>
        req<PublicEstimateDto>('POST', `/api/public/estimates/${token}/payment-confirmed`),
    getPublicEstimatePdf: (token: string) => req<{ pdfUrl: string }>('GET', `/api/public/estimates/${token}/pdf`),

    // Job line items (TREK-132)
    getJobLineItems: (jobId: string) => req<JobPricingSummaryDto>('GET', `/api/jobs/${jobId}/line-items`),
    addJobLineItem: (jobId: string, catalogItemId: string | null, description: string | null, quantity: number, unitPrice: number | null, afterHours: boolean) =>
        req<JobLineItemDto>('POST', `/api/jobs/${jobId}/line-items`, { catalogItemId, description, quantity, unitPrice, afterHours }),
    removeJobLineItem: (jobId: string, lineItemId: string) =>
        req<void>('DELETE', `/api/jobs/${jobId}/line-items/${lineItemId}`),

    // Estimates & quote approval (TREK-78)
    getEstimate: (jobId: string) => req<EstimateDto | null>('GET', `/api/jobs/${jobId}/estimate`).catch(() => null),
    sendEstimate: (jobId: string) => req<EstimateDto>('POST', `/api/jobs/${jobId}/estimate`),
    createEstimatePaymentIntent: (jobId: string) =>
        req<PaymentIntentResponse>('POST', `/api/jobs/${jobId}/estimate/payment-intent`),
    approveEstimate: (jobId: string) => req<EstimateDto>('POST', `/api/jobs/${jobId}/estimate/approve`),
    requestEstimateChanges: (jobId: string, note: string | null) =>
        req<EstimateDto>('POST', `/api/jobs/${jobId}/estimate/request-changes`, { note }),
    downloadEstimatePdf: async (jobId: string, estimateNumber: number) => {
        const token = getToken();
        const res = await fetch(`${BASE}/api/jobs/${jobId}/estimate/pdf`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Could not download the estimate PDF.');
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Estimate-E${estimateNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
    },

    // Automated invoicing (TREK-79)
    getInvoice: (jobId: string) => req<InvoiceDto | null>('GET', `/api/jobs/${jobId}/invoice`).catch(() => null),
    downloadInvoicePdf: async (jobId: string, invoiceNumber: number) => {
        const token = getToken();
        const res = await fetch(`${BASE}/api/jobs/${jobId}/invoice/pdf`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Could not download the invoice PDF.');
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Invoice-INV${invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
    },

    // Partial payments (TREK-80)
    getPaymentSchedule: (jobId: string) => req<PaymentScheduleDto | null>('GET', `/api/jobs/${jobId}/payment-milestones`).catch(() => null),
    splitJobIntoMilestones: (jobId: string, milestones: { label: string; amount: number; dueDescription?: string | null }[]) =>
        req<PaymentScheduleDto>('POST', `/api/jobs/${jobId}/payment-milestones`, { milestones }),
    createMilestoneIntent: (jobId: string, milestoneId: string) =>
        req<CreateMilestoneIntentResponse>('POST', `/api/jobs/${jobId}/payment-milestones/${milestoneId}/intent`),
    confirmMilestoneCapture: (jobId: string, milestoneId: string) =>
        req<PaymentScheduleDto>('POST', `/api/jobs/${jobId}/payment-milestones/${milestoneId}/confirm`),

    // Payouts
    getPayoutStatus: () =>
        req<ConnectStatusResponse>('GET', '/api/payouts/status'),
    getPayoutHistory: () =>
        req<PayoutHistoryItem[]>('GET', '/api/payouts/history'),
    startOnboarding: (returnUrl: string, refreshUrl: string) =>
        req<{ url: string }>('POST', '/api/payouts/onboard', { returnUrl, refreshUrl }),
    refreshOnboardingStatus: () =>
        req<ConnectStatusResponse>('POST', '/api/payouts/onboard/refresh', {}),
    retryPendingPayouts: () =>
        req<RetryPayoutsResult>('POST', '/api/payouts/retry-pending', {}),
    retryPayout: (jobId: string) =>
        req<{ message: string }>('POST', `/api/payouts/retry/${jobId}`, {}),
    resetContractorPayoutAccount: (contractorId: string) =>
        req<{ message: string }>('POST', `/api/payouts/admin/${contractorId}/reset-account`, {}),

    // Dumpster rental
    getDumpsterVendors: (lat: number, lng: number) =>
        req<DumpsterVendorResponse[]>('GET', `/api/dumpster/vendors?lat=${lat}&lng=${lng}`),
    // Vendor portal
    getVendorOrders: () =>
        req<any[]>('GET', '/api/dumpster/vendor/orders'),
    getVendorProfile: () =>
        req<any>('GET', '/api/dumpster/vendor/profile'),
    vendorAcceptOrder: (orderId: string) =>
        req<any>('POST', `/api/dumpster/vendor/orders/${orderId}/accept`, {}),
    vendorDeclineOrder: (orderId: string) =>
        req<any>('POST', `/api/dumpster/vendor/orders/${orderId}/decline`, {}),
    vendorMarkDelivered: (orderId: string) =>
        req<any>('POST', `/api/dumpster/vendor/orders/${orderId}/mark-delivered`, {}),
    vendorMarkPickupComplete: (orderId: string) =>
        req<any>('POST', `/api/dumpster/vendor/orders/${orderId}/mark-pickup-complete`, {}),
    getDumpsterQuote: (sizeId: string, rentalDays: number) =>
        req<DumpsterQuoteResponse>('GET', `/api/dumpster/quote?sizeId=${sizeId}&rentalDays=${rentalDays}`),
    bookDumpster: (body: BookDumpsterRequest) =>
        req<DumpsterOrderResponse>('POST', '/api/dumpster/book', body),
    getDumpsterPaymentIntent: (orderId: string) =>
        req<{ clientSecret: string; paymentIntentId: string; amount: number }>('POST', `/api/dumpster/orders/${orderId}/payment-intent`, {}),
    getMyDumpsterOrders: () =>
        req<DumpsterOrderResponse[]>('GET', '/api/dumpster/my-orders'),
    requestDumpsterPickup: (orderId: string, notes: string) =>
        req<DumpsterOrderResponse>('POST', `/api/dumpster/orders/${orderId}/request-pickup`, { notes }),
    cancelDumpsterOrder: (orderId: string, reason?: string) =>
        req<DumpsterOrderResponse>('POST', `/api/dumpster/orders/${orderId}/cancel`, { reason }),
    updateDumpsterOrder: (orderId: string, body: UpdateDumpsterOrderRequest) =>
        req<DumpsterOrderResponse>('PUT', `/api/dumpster/orders/${orderId}`, body),
    approveDumpsterHours: (jobId: string, approved: boolean) =>
        req<any>('POST', `/api/dumpster/loading-jobs/${jobId}/approve-hours`, { approved }),
    // Contractor loading jobs
    getOpenLoadingJobs: (lat: number, lng: number, radius: number = 500) =>
        req<LoadingJobResponse[]>('GET', `/api/dumpster/loading-jobs?lat=${lat}&lng=${lng}&radius=${radius}`),
    getMyLoadingJobs: () =>
        req<LoadingJobResponse[]>('GET', '/api/dumpster/loading-jobs/mine'),
    acceptLoadingJob: (jobId: string) =>
        req<LoadingJobResponse>('POST', `/api/dumpster/loading-jobs/${jobId}/accept`, {}),
    updateLoadingJobStatus: (jobId: string, status: string) =>
        req<any>('POST', `/api/dumpster/loading-jobs/${jobId}/status`, { status }),
    clockInLoadingJob: (jobId: string) =>
        req<any>('POST', `/api/dumpster/loading-jobs/${jobId}/clock-in`, {}),
    clockOutLoadingJob: (jobId: string) =>
        req<any>('POST', `/api/dumpster/loading-jobs/${jobId}/clock-out`, {}),
    // Vendor documents (vendor self-service)
    getVendorDocumentStatus: () =>
        req<VendorDocumentStatusResponse>('GET', '/api/dumpster/vendor/documents'),
    submitVendorDocument: (docType: string, photoBase64: string) =>
        req<VendorDocumentStatusResponse>('POST', '/api/dumpster/vendor/documents', { docType, photoBase64 }),
    // Vendor Stripe Connect
    getVendorConnectStatus: () =>
        req<VendorConnectStatusResponse>('GET', '/api/dumpster/vendor/connect/status'),
    getVendorOnboardingUrl: (returnUrl: string, refreshUrl: string) =>
        req<{ url: string }>('GET', `/api/dumpster/vendor/connect/onboarding-url?returnUrl=${encodeURIComponent(returnUrl)}&refreshUrl=${encodeURIComponent(refreshUrl)}`),
    refreshVendorConnect: () =>
        req<VendorConnectStatusResponse>('POST', '/api/dumpster/vendor/connect/refresh', {}),
    getVendorPayoutHistory: () =>
        req<VendorPayoutHistoryItem[]>('GET', '/api/dumpster/vendor/connect/payout-history'),
    adminUpdateVendorRate: (vendorId: string, rateType: string, rateAmount?: number) =>
        req<DumpsterVendorResponse>('PATCH', `/api/dumpster/admin/vendors/${vendorId}/rate`, { rateType, rateAmount }),

    // Admin
    getAllDumpsterOrders: () =>
        req<DumpsterOrderResponse[]>('GET', '/api/dumpster/admin/orders'),
    getAdminLoadingJobs: () =>
        req<LoadingJobResponse[]>('GET', '/api/dumpster/admin/loading-jobs'),
    getAllDumpsterVendors: () =>
        req<DumpsterVendorResponse[]>('GET', '/api/dumpster/admin/vendors'),
    registerDumpsterVendor: (body: RegisterVendorRequest) =>
        req<DumpsterVendorResponse>('POST', '/api/dumpster/admin/vendors', body),
    updateDumpsterVendor: (vendorId: string, body: UpdateVendorRequest) =>
        req<DumpsterVendorResponse>('PUT', `/api/dumpster/admin/vendors/${vendorId}`, body),
    addDumpsterSize: (vendorId: string, body: AddDumpsterSizeRequest) =>
        req<DumpsterVendorResponse>('POST', `/api/dumpster/admin/vendors/${vendorId}/sizes`, body),
    getAllVendorDocuments: () =>
        req<VendorDocumentItem[]>('GET', '/api/dumpster/admin/vendor-documents'),
    getAdminVendorDocuments: (vendorId: string) =>
        req<VendorDocumentItem>('GET', `/api/dumpster/admin/vendors/${vendorId}/documents`),
    reviewVendorDocument: (vendorId: string, docType: string, approved: boolean, reason?: string) =>
        req<VendorDocumentItem>('POST', `/api/dumpster/admin/vendors/${vendorId}/documents/${docType}/review`, { approved, reason }),

    // Product preorders (guest checkout, no auth required)
    getPreorderOfferings: () =>
        req<PreorderOfferingResponse[]>('GET', '/api/product-preorder/offerings'),
    createProductPreorder: (body: CreateProductPreorderRequest) =>
        req<CreateProductPreorderResponse>('POST', '/api/product-preorder', body),
    confirmProductPreorder: (id: string, paymentIntentId: string) =>
        req<ProductPreorderResponse>('POST', `/api/product-preorder/${id}/confirm`, { paymentIntentId }),
    getAllProductPreorders: () =>
        req<ProductPreorderResponse[]>('GET', '/api/product-preorder/admin'),
    updateProductPreorderFulfillment: (id: string, fulfillmentStatus: string) =>
        req<ProductPreorderResponse>('PUT', `/api/product-preorder/admin/${id}/fulfillment`, { fulfillmentStatus }),

    // Service area coverage (guest-facing check + admin rule management)
    checkServiceArea: (state?: string, county?: string, zip?: string, lat?: number, lng?: number) => {
        const params = new URLSearchParams();
        if (state) params.set('state', state);
        if (county) params.set('county', county);
        if (zip) params.set('zip', zip);
        if (lat != null) params.set('lat', String(lat));
        if (lng != null) params.set('lng', String(lng));
        return req<{ covered: boolean }>('GET', `/api/service-area/check?${params.toString()}`);
    },
    getServiceAreaRules: () => req<ServiceAreaRuleResponse[]>('GET', '/api/service-area/admin/rules'),
    addServiceAreaRule: (type: string, mode: string, state?: string, county?: string, zip?: string,
        centerLat?: number, centerLng?: number, radiusMiles?: number, centerAddress?: string) =>
        req<ServiceAreaRuleResponse>('POST', '/api/service-area/admin/rules',
            { type, mode, state, county, zip, centerLat, centerLng, radiusMiles, centerAddress }),
    removeServiceAreaRule: (id: string) =>
        req<void>('DELETE', `/api/service-area/admin/rules/${id}`),

    // Business license compliance (jurisdiction-tagged, standalone from any specific rule)
    getBusinessLicenses: () => req<BusinessLicenseResponse[]>('GET', '/api/business-licenses'),
    uploadBusinessLicense: (body: {
        state?: string; county?: string; city?: string; licenseNumber?: string;
        photoBase64: string; issuedDate?: string; expiryDate?: string;
    }) => req<BusinessLicenseResponse>('POST', '/api/business-licenses', body),
    reviewBusinessLicense: (id: string, approved: boolean, reason?: string) =>
        req<BusinessLicenseResponse>('POST', `/api/business-licenses/${id}/review`, { approved, reason }),
    removeBusinessLicense: (id: string) =>
        req<void>('DELETE', `/api/business-licenses/${id}`),

    // Document verification
    getMyDocumentStatus: () =>
        req<DocumentStatusResponse>('GET', '/api/documents/status'),
    submitLicense: (licenseNumber: string, expiryDate: string, photoBase64: string) =>
        req<DocumentStatusResponse>('POST', '/api/documents/license', { licenseNumber, expiryDate, photoBase64 }),
    submitInsurance: (provider: string, policyNumber: string, expiryDate: string, photoBase64: string) =>
        req<DocumentStatusResponse>('POST', '/api/documents/insurance', { provider, policyNumber, expiryDate, photoBase64 }),
    submitVehicle: (make: string, model: string, year: number, color: string, plate: string, photoBase64?: string) =>
        req<DocumentStatusResponse>('POST', '/api/documents/vehicle', { make, model, year, color, plate, photoBase64 }),
    getContractorDocuments: () =>
        req<ContractorDocumentItem[]>('GET', '/api/documents/admin/all'),
    getExpiringDocuments: (withinDays: number) =>
        req<ContractorDocumentItem[]>('GET', `/api/documents/admin/expiring?withinDays=${withinDays}`),
    approveLicense: (contractorId: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/license/${contractorId}/approve`, {}),
    rejectLicense: (contractorId: string, reason: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/license/${contractorId}/reject`, { reason }),
    approveInsurance: (contractorId: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/insurance/${contractorId}/approve`, {}),
    rejectInsurance: (contractorId: string, reason: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/insurance/${contractorId}/reject`, { reason }),
    approveVehicle: (contractorId: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/vehicle/${contractorId}/approve`, {}),
    rejectVehicle: (contractorId: string, reason: string) =>
        req<ContractorDocumentItem>('POST', `/api/documents/admin/vehicle/${contractorId}/reject`, { reason }),

    // Tier / Gamification
    getMyTierStats: () =>
        req<ContractorTierStats>('GET', '/api/tier/stats'),
    getTierDistribution: () =>
        req<TierDistribution>('GET', '/api/tier/distribution'),

    // Messaging
    getMessages: (jobId: string) =>
        req<MessageItem[]>('GET', `/api/messages/${jobId}`),
    sendMessage: (jobId: string, text: string) =>
        req<MessageItem>('POST', `/api/messages/${jobId}`, { text }),
    getUnreadCount: (jobId: string) =>
        req<{ count: number }>('GET', `/api/messages/${jobId}/unread-count`),

    // Customer profile & subscriptions
    getCustomerProfile: () =>
        req<CustomerProfile>('GET', '/api/customers/profile'),
    updateCustomerProfile: (data: { name?: string; phoneNumber?: string; notificationPrefs?: { jobUpdates: boolean; promotions: boolean }; defaultAddress?: string; defaultLat?: number; defaultLng?: number }) =>
        req<CustomerProfile>('PUT', '/api/customers/profile', data),
    changePassword: (currentPassword: string, newPassword: string) =>
        req<void>('POST', '/api/customers/change-password', { currentPassword, newPassword }),
    changeEmail: (currentPassword: string, newEmail: string) =>
        req<void>('PUT', '/api/customers/email', { currentPassword, newEmail }),
    deleteAccount: (currentPassword: string) =>
        req<void>('DELETE', '/api/customers/account', { currentPassword }),
    // Admin: customer management
    adminGetCustomers: (search?: string) =>
        req<AdminCustomerListItem[]>('GET', `/api/admin/customers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    adminGetCustomer: (id: string) =>
        req<AdminCustomerDetail>('GET', `/api/admin/customers/${id}`),
    adminUpdateCustomer: (id: string, data: { name?: string; email?: string; phoneNumber?: string }) =>
        req<void>('PUT', `/api/admin/customers/${id}`, data),
    adminCancelCustomerSubscription: (id: string) =>
        req<void>('DELETE', `/api/admin/customers/${id}/subscription`),
    getSubscriptionPlans: (all = false) =>
        req<SubscriptionPlan[]>('GET', `/api/subscription-plans${all ? '?all=true' : ''}`),
    getActiveSubscription: () =>
        req<CustomerSubscriptionDetail | null>('GET', '/api/customers/subscription'),
    subscribe: (planId: string) =>
        req<CreateSubscriptionResult>('POST', '/api/customers/subscription', { planId }),
    cancelSubscription: (immediately = false) =>
        req<void>('DELETE', `/api/customers/subscription?immediately=${immediately}`),
    changePlan: (planId: string) =>
        req<void>('PUT', '/api/customers/subscription', { planId }),

    // Ads
    getAds: () =>
        req<AdItem[]>('GET', '/api/ads'),
    adminGetAds: () =>
        req<AdItem[]>('GET', '/api/ads/all'),
    adminCreateAd: (data: Omit<AdItem, 'id'>) =>
        req<AdItem>('POST', '/api/ads', data),
    adminUpdateAd: (id: string, data: Omit<AdItem, 'id'>) =>
        req<AdItem>('PUT', `/api/ads/${id}`, data),
    adminDeleteAd: (id: string) =>
        req<void>('DELETE', `/api/ads/${id}`),

    // QuickBooks
    qboStatus: () =>
        req<QboConnectionStatus>('GET', '/api/admin/qbo/status'),
    qboGetConnectUrl: (environment: 'sandbox' | 'production' = 'sandbox') =>
        req<{ url: string; state: string }>('GET', `/api/admin/qbo/connect?environment=${environment}`),
    qboCallback: (code: string, realmId: string, state: string) =>
        req<QboConnectionStatus>('POST', '/api/admin/qbo/callback', { code, realmId, state }),
    qboDisconnect: () =>
        req<void>('POST', '/api/admin/qbo/disconnect'),
    qboGetAccounts: () =>
        req<QboAccount[]>('GET', '/api/admin/qbo/accounts'),
    qboGetItems: () =>
        req<QboItem[]>('GET', '/api/admin/qbo/items'),
    qboGetMappings: () =>
        req<QboAccountMapping[]>('GET', '/api/admin/qbo/mappings'),
    qboSaveMappings: (mappings: QboAccountMapping[]) =>
        req<QboAccountMapping[]>('PUT', '/api/admin/qbo/mappings', mappings),
    qboGetSyncErrors: () =>
        req<QboSyncError[]>('GET', '/api/admin/qbo/sync-errors'),
    qboRetryError: (id: string) =>
        req<void>('POST', `/api/admin/qbo/sync-errors/${id}/retry`),
    qboRetryAll: () =>
        req<void>('POST', '/api/admin/qbo/sync-errors/retry-all'),

    sfdcStatus: () =>
        req<SalesforceConnectionStatus>('GET', '/api/admin/salesforce/status'),
    sfdcGetConnectUrl: (environment: 'sandbox' | 'production' = 'sandbox') =>
        req<{ url: string; state: string }>('GET', `/api/admin/salesforce/connect?environment=${environment}`),
    sfdcDisconnect: () =>
        req<void>('POST', '/api/admin/salesforce/disconnect'),
    sfdcGetSyncErrors: () =>
        req<CrmSyncError[]>('GET', '/api/admin/salesforce/sync-errors'),
    sfdcRetryError: (id: string) =>
        req<{ success: boolean }>('POST', `/api/admin/salesforce/sync-errors/${id}/retry`),
    sfdcRetryAll: () =>
        req<void>('POST', '/api/admin/salesforce/sync-errors/retry-all'),

    crmGetMappings: (provider: string) =>
        req<CrmFieldMapping[]>('GET', `/api/admin/crm/mappings?provider=${encodeURIComponent(provider)}`),
    crmSaveMapping: (mapping: { provider: string; entityType: string; targetObject: string; fieldMappings: Record<string, string> }) =>
        req<CrmFieldMapping>('POST', '/api/admin/crm/mappings', mapping),

    // TREK-68: Contractor availability
    getMyAvailability: () =>
        req<ContractorAvailabilityResponse>('GET', '/api/contractor/availability'),
    setMyAvailabilitySlots: (slots: AvailabilitySlot[]) =>
        req<ContractorAvailabilityResponse>('PUT', '/api/contractor/availability', { slots }),
    addBlockedDate: (date: string, reason?: string) =>
        req<ContractorAvailabilityResponse>('POST', '/api/contractor/availability/blocked-dates', { date, reason }),
    removeBlockedDate: (date: string) =>
        req<ContractorAvailabilityResponse>('DELETE', `/api/contractor/availability/blocked-dates/${date}`),

    // TREK-66: Admin calendar
    getCalendarJobs: (start: string, end: string) =>
        req<CalendarJob[]>('GET', `/api/admin/calendar?start=${start}&end=${end}`),
    getUnassignedJobs: () =>
        req<CalendarJob[]>('GET', '/api/admin/calendar/unassigned'),
    getSelfAssignedJobs: () =>
        req<CalendarJob[]>('GET', '/api/admin/calendar/self-assigned'),
    getContractorAvailabilities: () =>
        req<ContractorAvailabilityItem[]>('GET', '/api/admin/calendar/contractors'),

    // TREK-67: Dispatch board
    assignJob: (jobId: string, contractorId: string, scheduledDate?: string) =>
        req<{ id: string; contractorId: string; contractorName: string; scheduledDate?: string; status: string }>(
            'POST', `/api/admin/jobs/${jobId}/assign`, { contractorId, scheduledDate }),
    unassignJob: (jobId: string) =>
        req<{ id: string; status: string }>('POST', `/api/admin/jobs/${jobId}/unassign`),
    rescheduleJob: (jobId: string, scheduledDate: string | null, scheduledTimeSlot?: string) =>
        req<{ id: string; scheduledDate?: string; scheduledTimeSlot?: string }>(
            'PATCH', `/api/admin/jobs/${jobId}/schedule`, { scheduledDate, scheduledTimeSlot }),

    // TREK-107/108: Payout override & contractor rate
    setPayoutOverride: (jobId: string, payoutOverride: number | null) =>
        req<{ jobId: string; payoutOverride: number | null }>(
            'PATCH', `/api/admin/jobs/${jobId}/payout`, { payoutOverride }),
    setContractorRate: (contractorId: string, rateType: string, rateAmount: number | null, serviceType?: string) =>
        req<{ contractorId: string; rateType: string; rateAmount: number | null; serviceType?: string }>(
            'PATCH', `/api/admin/contractors/${contractorId}/rate`, { rateType, rateAmount, serviceType }),
    deleteContractorServiceRate: (contractorId: string, serviceType: string) =>
        req<{ contractorId: string; serviceType: string }>(
            'DELETE', `/api/admin/contractors/${contractorId}/rate/${serviceType}`),
    // Opt-in per-contractor territory — all fields null clears it back to unrestricted.
    adminSetContractorServiceArea: (contractorId: string, data: { serviceAreaAddress?: string | null; serviceAreaLat?: number | null; serviceAreaLng?: number | null; serviceAreaRadiusMiles?: number | null }) =>
        req<{ contractorId: string; serviceAreaAddress?: string; serviceAreaLat?: number; serviceAreaLng?: number; serviceAreaRadiusMiles?: number }>(
            'PATCH', `/api/admin/contractors/${contractorId}/service-area`, data),
    getPlatformConfig: () =>
        req<{ loadingHourlyRate: number }>('GET', '/api/admin/platform'),
    updatePlatformConfig: (loadingHourlyRate: number) =>
        req<{ loadingHourlyRate: number }>('PATCH', '/api/admin/platform', { loadingHourlyRate }),

    // TREK-69: Recurring jobs
    getRecurringSchedules: () =>
        req<RecurringSchedule[]>('GET', '/api/jobs/recurring'),
    createRecurringSchedule: (data: {
        serviceType: string; pickupAddress: string; pickupLat: number; pickupLng: number;
        estimatedUnits: number; scheduledTimeSlot?: string; interval: string; firstOccurrenceDate: string;
    }) => req<RecurringSchedule>('POST', '/api/jobs/recurring', data),
    updateRecurringSchedule: (id: string, data: { isPaused?: boolean; nextOccurrenceDate?: string }) =>
        req<RecurringSchedule>('PUT', `/api/jobs/recurring/${id}`, data),
    deleteRecurringSchedule: (id: string) =>
        req<void>('DELETE', `/api/jobs/recurring/${id}`),

    // TREK-70: Route optimization
    getOptimizedRoute: (contractorId: string, date: string) =>
        req<RouteOptimizationResult>('GET', `/api/admin/contractors/${contractorId}/route?date=${date}`),
    applyRoute: (contractorId: string, date: string, jobIds: string[]) =>
        req<RouteOptimizationResult>('POST', `/api/admin/contractors/${contractorId}/route/apply?date=${date}`, { jobIds }),
    getStripePortalUrl: () =>
        req<{ url: string }>('POST', '/api/customers/stripe-portal', {}),

    // Admin: subscription plan management
    adminCreateSubscriptionPlan: (data: { name: string; description?: string; monthlyPrice: number; billingInterval?: string; includedPickups?: number; serviceLabel?: string; discountPercent?: number; priorityScheduling?: boolean; sortOrder?: number }) =>
        req<SubscriptionPlan>('POST', '/api/subscription-plans', data),
    adminUpdateSubscriptionPlan: (id: string, data: { name?: string; description?: string; monthlyPrice?: number; billingInterval?: string; includedPickups?: number; serviceLabel?: string; discountPercent?: number; priorityScheduling?: boolean; sortOrder?: number; isActive?: boolean }) =>
        req<SubscriptionPlan>('PUT', `/api/subscription-plans/${id}`, data),
    adminDeleteSubscriptionPlan: (id: string) =>
        req<void>('DELETE', `/api/subscription-plans/${id}`),

    // Background checks
    getBackgroundChecks: () =>
        req<ContractorBgCheckItem[]>('GET', '/api/background-check/admin/all'),
    getMyBackgroundCheckStatus: () =>
        req<BackgroundCheckStatusResponse>('GET', '/api/background-check/status'),
    initiateMyBackgroundCheck: () =>
        req<BackgroundCheckStatusResponse>('POST', '/api/background-check/invite', {}),
    adminInviteBackgroundCheck: (contractorId: string) =>
        req<BackgroundCheckStatusResponse>('POST', `/api/background-check/admin/invite/${contractorId}`, {}),
    adminApproveBackgroundCheck: (contractorId: string, notes: string) =>
        req<BackgroundCheckStatusResponse>('POST', `/api/background-check/admin/approve/${contractorId}`, { notes }),
    adminSuspendBackgroundCheck: (contractorId: string, notes: string) =>
        req<BackgroundCheckStatusResponse>('POST', `/api/background-check/admin/suspend/${contractorId}`, { notes }),
    adminRerunBackgroundCheck: (contractorId: string) =>
        req<BackgroundCheckStatusResponse>('POST', `/api/background-check/admin/rerun/${contractorId}`, {}),

    // ── Disclosures ────────────────────────────────────────────────────────────
    getPendingDisclosures: () => req<DisclosureItem[]>('GET', '/api/disclosures/pending'),
    getMyDisclosureAcceptances: () => req<DisclosureAcceptance[]>('GET', '/api/disclosures/my-acceptances'),
    acceptDisclosure: (id: string, arbitrationOptOut = false) =>
        req<DisclosureAcceptance>('POST', `/api/disclosures/${id}/accept`, { arbitrationOptOut }),
    adminGetDisclosures: () => req<DisclosureItem[]>('GET', '/api/disclosures/admin'),
    adminUpdateDisclosure: (id: string, title: string, body: string) =>
        req<DisclosureItem>('PUT', `/api/disclosures/admin/${id}`, { title, body }),
    adminGetContractorDisclosureStatus: (contractorId: string) =>
        req<any>('GET', `/api/disclosures/admin/contractor/${contractorId}`),

    // ── Legal pages (Privacy Policy / Terms of Service) ─────────────────────────
    getLegalDocument: (type: 'PrivacyPolicy' | 'TermsOfService') =>
        req<LegalDocumentItem>('GET', `/api/legal/${type}`),
    adminGetLegalDocuments: () => req<LegalDocumentItem[]>('GET', '/api/legal/admin'),
    adminUpdateLegalDocument: (id: string, title: string, body: string) =>
        req<LegalDocumentItem>('PUT', `/api/legal/admin/${id}`, { title, body }),

    // User management
    adminGetAllUsers: (search?: string) =>
        req<AdminAllUsersResponse>('GET', `/api/admin/users/all${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    adminGetAdmins: () => req<AdminListItem[]>('GET', '/api/admin/users/admins'),
    adminGetUserCustomers: () => req<UserManagementItem[]>('GET', '/api/admin/users/customers'),
    adminGetUserContractors: () => req<UserManagementItem[]>('GET', '/api/admin/users/contractors'),
    adminGetUserVendors: () => req<UserManagementItem[]>('GET', '/api/admin/users/vendors'),
    adminCreateUser: (data: { name: string; email: string; password?: string; role: string; phoneNumber?: string }) =>
        req<{ id: string; name: string; email: string; role: string }>('POST', '/api/admin/users', data),
    adminActivateUser: (id: string, type: string) =>
        req<{ active: boolean }>('POST', `/api/admin/users/${id}/activate?type=${type}`),
    adminDeactivateUser: (id: string, type: string) =>
        req<{ active: boolean }>('POST', `/api/admin/users/${id}/deactivate?type=${type}`),
    adminResetUserPassword: (id: string, type: string) =>
        req<{ message: string }>('POST', `/api/admin/users/${id}/reset-password?type=${type}`),
    adminDeleteUser: (id: string, type: string) =>
        req<{ deleted: boolean }>('DELETE', `/api/admin/users/${id}?type=${type}`),
    adminUpdateUser: (id: string, type: string, data: { name: string; email: string; phoneNumber?: string }) =>
        req<{ id: string; name: string; email: string }>('PUT', `/api/admin/users/${id}?type=${type}`, data),

    // Admin-initiated (phone/CSR) booking
    adminCreateJob: (customerId: string, job: CreateJobDto) =>
        req<Job>('POST', '/api/admin/jobs', { customerId, job }),
    adminCreateJobPaymentIntent: (jobId: string) =>
        req<PaymentIntentResponse>('POST', `/api/admin/jobs/${jobId}/payment-intent`),
    adminConfirmJobAuthorization: (jobId: string) =>
        req<void>('POST', `/api/admin/jobs/${jobId}/confirm-authorization`, {}),

    // ── App Marketplace (Tenant Admin) ──────────────────────────────────────────
    getMarketplaceCatalog: () =>
        req<MarketplaceAppCatalogDto[]>('GET', '/api/tenant-apps/catalog'),
    getMarketplaceAppDetail: (appId: string) =>
        req<MarketplaceAppDetailDto>('GET', `/api/tenant-apps/catalog/${appId}`),
    getInstalledApps: () =>
        req<TenantAppInstallationDto[]>('GET', '/api/tenant-apps/installed'),
    installApp: (appId: string) =>
        req<InstallAppResponse>('POST', `/api/tenant-apps/${appId}/install`, {}),
    configureApp: (appId: string, fields: Record<string, string>) =>
        req<InstallAppResponse>('POST', `/api/tenant-apps/${appId}/configure`, { fields }),
    // Compliance note: this is the ONLY marketplace purchase path, and it is web-only by design
    // — see TenantAppSubscription's doc comment in Models.cs. Never call this from a mobile client.
    checkoutApp: (appId: string, successUrl: string, cancelUrl: string) =>
        req<AppCheckoutResponse>('POST', `/api/tenant-apps/${appId}/checkout?successUrl=${encodeURIComponent(successUrl)}&cancelUrl=${encodeURIComponent(cancelUrl)}`, {}),
    uninstallApp: (appId: string, immediately = false) =>
        req<void>('POST', `/api/tenant-apps/${appId}/uninstall?immediately=${immediately}`, {}),
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthResponse { token: string; userId: string; name: string; email: string; role: string; }
export interface UserManagementItem { id: string; name: string; email: string; phone?: string; role: string; isActive: boolean; status?: string; createdAt: string; }
export interface AdminListItem { id: string; name: string; email: string; isActive: boolean; createdAt: string; }
export interface AdminAllUsersResponse { customers: UserManagementItem[]; contractors: UserManagementItem[]; vendors: UserManagementItem[]; admins: UserManagementItem[]; }
export interface LoginResponse { mfaRequired: boolean; pendingToken?: string; token?: string; userId?: string; name?: string; email?: string; role?: string; }
export interface RegisterCustomerDto { name: string; email: string; password: string; phoneNumber?: string; }
export interface RegisterContractorDto { name: string; email: string; password: string; phoneNumber?: string; vehicleDescription?: string; }
export interface QuoteResponse {
    basePrice: number; pricePerUnit: number; unitLabel: string; total: number; subscriberTotal: number | null;
    serviceFeeAmount: number; serviceFeeLabel: string; taxAmount: number; grandTotal: number;
}
export interface CreateJobDto {
    serviceType: string; pickupAddress: string; pickupLat: number; pickupLng: number; estimatedUnits: number;
    scheduledTimeSlot?: string; tipAmount: number;
    pickupCity?: string; pickupState?: string; pickupCounty?: string; pickupPostalCode?: string; pickupCountry?: string;
    donationCenterId?: string;
    shreddingDocumentCategories?: string[];
    shreddingRequestType?: string;
    shreddingCustomerType?: string;
    shreddingSizeId?: string;
    shreddingServiceLocation?: string;
    giftVoucherCode?: string;
}

export interface GiftVoucherPurchaseResponse {
    clientSecret: string; paymentIntentId: string; amount: number; publishableKey: string;
}
export interface GiftVoucherDto {
    id: string; code: string; serviceTypeName: string; amount: number;
    purchaserName: string; recipientName?: string; recipientEmail?: string; message?: string;
    paymentStatus: string; redeemedAt?: string; redeemedByName?: string; isVoided: boolean; createdAt: string;
    shreddingSizeName?: string; shreddingRequestType?: string; shreddingEstimatedUnits?: number;
}
export interface ValidateGiftVoucherResponse {
    valid: boolean; error?: string; amount?: number;
}

export interface DonationCenterDto {
    id: string; name: string; address: string; lat?: number; lng?: number; phone?: string; taxId?: string;
    isActive: boolean; sortOrder: number;
    city?: string; state?: string; county?: string; postalCode?: string; country?: string;
    description?: string;
}
export interface DonationCenterGeo {
    lat?: number; lng?: number; city?: string; state?: string; county?: string; postalCode?: string; country?: string;
}
export interface ShreddingBinDto {
    id: string; label: string; status: string; currentPropertyId?: string; createdAt: string;
    sizeId?: string; sizeName?: string;
}
export interface BinScanEventDto {
    id: string; binId: string; binLabel: string; jobId?: string; contractorId: string; contractorName: string;
    scanType: string; lat?: number; lng?: number; scannedAt: string; notes?: string;
}
export interface ShreddingBinSizeDto {
    id: string; name: string; price: number; availableToResidential: boolean; availableToBusiness: boolean; isActive: boolean;
}
export interface CustomerBinStatusDto {
    hasActiveBin: boolean; activeBinSizeName?: string; activeBinPrice?: number; retrievalFee: number;
}
export interface ShreddingBinLocationDto {
    id: string; label: string; sizeName?: string; status: string; lat: number; lng: number;
}
export interface ContractorSummary { id: string; name: string; phoneNumber?: string; vehicleDescription?: string; averageRating: number; totalRatings: number; currentLat?: number; currentLng?: number; isOnline: boolean; backgroundCheckStatus?: string; tier?: string; tierIcon?: string; }
export interface Rating { stars: number; comment?: string; createdAt: string; }

export interface Job {
    id: string;
    serviceType: string;
    status: string;
    paymentStatus: string;
    pickupAddress: string;
    pickupLat: number;
    pickupLng: number;
    estimatedUnits: number;
    actualUnits?: number;
    basePrice: number;
    tipAmount: number;
    totalPrice: number;
    serviceFeeAmount: number;
    serviceFeeLabel: string;
    taxAmount: number;
    grandTotal: number;
    adjustedPrice?: number;
    priceAdjustmentStatus?: string;
    scheduledTimeSlot?: string;
    requestedDate?: string;
    scheduledDate?: string;
    contractor?: ContractorSummary;
    rating?: Rating;
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
    photoUrl?: string;
    subscriptionCovered: boolean;
    earlyStartStatus?: string;
    donationCenter?: { id: string; name: string; address: string; lat?: number; lng?: number };
    shredding?: { categoriesConfirmed: boolean; requestType: string; serviceLocation?: string; sizeName?: string };
}

export interface DashboardStats { jobsToday: number; jobsThisWeek: number; revenueToday: number; revenueThisWeek: number; activeContractors: number; onlineContractors: number; averageRating: number; }

export interface JobListItem {
    id: string; serviceType: string; status: string; pickupAddress: string;
    pickupLat: number; pickupLng: number; totalPrice: number; customerName: string;
    contractorName?: string; estimatedUnits: number; actualUnits?: number; createdAt: string;
    startedAt?: string; completedAt?: string;
    priceAdjustmentStatus?: string;
    adjustedPrice?: number;
    serviceFeeAmount?: number;
    taxAmount?: number;
}

export interface PagedJobsResponse {
    items: JobListItem[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface ContractorListItem {
    id: string; name: string; email: string; phoneNumber?: string;
    vehicleDescription?: string; averageRating: number; totalRatings: number;
    isOnline: boolean; totalJobsCompleted: number; currentLat?: number;
    currentLng?: number; createdAt: string;
    tier?: string; tierIcon?: string; backgroundCheckStatus?: string;
    rateType?: string; rateAmount?: number;
    serviceRates: { serviceType: string; rateType: string; rateAmount?: number }[];
    docsApproved: boolean;
    skills?: ContractorSkillDto[];
    hasPayoutAccount?: boolean;
    payoutOnboardingComplete?: boolean;
    serviceAreaLat?: number;
    serviceAreaLng?: number;
    serviceAreaRadiusMiles?: number;
    serviceAreaAddress?: string;
}

// ── Contractor skills & certifications (TREK-75) ──────────────────────────────

export interface ContractorSkillDto {
    id: string;
    name: string;
    createdAt: string;
}

// ── Supply & inventory tracking (TREK-81) ──────────────────────────────────────────

export interface InventoryItemDto {
    id: string;
    name: string;
    category: string | null;
    quantityOnHand: number;
    reorderThreshold: number;
    unitCost: number;
    isLowStock: boolean;
    createdAt: string;
    updatedAt: string;
}

// ── Digital work orders & signature (TREK-88) ──────────────────────────────────────

export type WorkOrderStatusType = 'Pending' | 'Signed';

export interface WorkOrderDto {
    id: string;
    jobId: string;
    scopeOfWork: string;
    terms: string;
    status: WorkOrderStatusType;
    signedPdfUrl: string | null;
    signedAt: string | null;
    signatureLat: number | null;
    signatureLng: number | null;
    createdAt: string;
}

export interface InventoryUsageDto {
    id: string;
    inventoryItemId: string;
    inventoryItemName: string;
    jobId: string | null;
    quantityChange: number;
    reason: string | null;
    createdAt: string;
}

// ── Property Records (core primitive) ────────────────────────────────────────────

export type PropertyOccupancyStatusType = 'Unknown' | 'Occupied' | 'Vacant';

export interface PropertyDto {
    id: string;
    address: string;
    lat: number;
    lng: number;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
    occupancyStatus: PropertyOccupancyStatusType;
    customerId: string | null;
    customerName: string | null;
    externalPropertyId: string | null;
    externalSource: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreatePropertyRequest {
    address: string;
    lat: number;
    lng: number;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    occupancyStatus?: PropertyOccupancyStatusType;
    customerId?: string | null;
    externalPropertyId?: string | null;
    externalSource?: string | null;
    notes?: string | null;
}

export interface UpdatePropertyRequest {
    address: string;
    lat: number;
    lng: number;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    occupancyStatus: PropertyOccupancyStatusType;
    customerId?: string | null;
    notes?: string | null;
}

export interface PropertyHistoryItemDto {
    jobId: string;
    serviceTypeName: string;
    status: string;
    scheduledDate: string | null;
    completedAt: string | null;
}

// ── Compliance Documents (core primitive) ────────────────────────────────────────

export type ComplianceHolderTypeType = 'Tenant' | 'Contractor' | 'Vendor' | 'Property';

export interface ComplianceDocumentDto {
    id: string;
    holderType: string;
    holderId: string;
    documentType: string;
    documentUrl: string;
    issuedDate: string | null;
    expiryDate: string | null;
    status: string;
    rejectionReason: string | null;
    notes: string | null;
    uploadedAt: string;
    isExpiringSoon: boolean;
    isExpired: boolean;
}

export interface CreateComplianceDocumentRequest {
    holderType: ComplianceHolderTypeType;
    holderId?: string;
    documentType: string;
    fileBase64: string;
    fileName: string;
    issuedDate?: string | null;
    expiryDate?: string | null;
    notes?: string | null;
}

// ── Photo Checklists (core primitive) ────────────────────────────────────────────

export interface ShotSpecDto {
    label: string;
    required: boolean;
    description: string | null;
}

export interface PhotoChecklistTemplateDto {
    id: string;
    name: string;
    shotSpecs: ShotSpecDto[];
    isActive: boolean;
    createdAt: string;
}

export interface SavePhotoChecklistTemplateRequest {
    name: string;
    shotSpecs: ShotSpecDto[];
    isActive: boolean;
}

export interface JobPhotoSubmissionDto {
    id: string;
    jobId: string;
    label: string;
    photoUrl: string;
    lat: number | null;
    lng: number | null;
    capturedAt: string;
}

export interface SubmitJobPhotoRequest {
    label: string;
    photoUrl: string;
    lat?: number | null;
    lng?: number | null;
}

export interface JobPhotoChecklistStatusDto {
    templateId: string | null;
    templateName: string | null;
    shotSpecs: ShotSpecDto[];
    submissions: JobPhotoSubmissionDto[];
    allRequiredSubmitted: boolean;
}

// ── Job SLA / Due-Date Tracking (core primitive) ─────────────────────────────────

export type JobPriorityType = 'Normal' | 'Rush' | 'Escalated';

export interface JobSlaDto {
    jobId: string;
    dueDate: string;
    priority: JobPriorityType;
    escalatedAt: string | null;
    jobStatus: string;
    serviceTypeName: string;
    contractorName: string | null;
    isOverdue: boolean;
}

// ── Client Portal (core infrastructure) ──────────────────────────────────────────

export interface ClientUserDto {
    id: string;
    name: string;
    email: string;
    phoneNumber: string | null;
    isActive: boolean;
    createdAt: string;
}

export interface RegisterClientRequest {
    name: string;
    email: string;
    password: string;
    phoneNumber?: string | null;
}

export interface ClientPropertyAccessDto {
    clientUserId: string;
    clientName: string;
    propertyId: string;
    propertyAddress: string;
    grantedAt: string;
}

export interface ClientPortalPropertyDto {
    id: string;
    address: string;
    city: string | null;
    state: string | null;
    occupancyStatus: string;
}

export interface ContractorJob {
    id: string;
    serviceType: string;
    status: string;
    paymentStatus: string;
    pickupAddress: string;
    pickupLat: number;
    pickupLng: number;
    estimatedUnits: number;
    actualUnits?: number;
    priceAdjustmentStatus?: string;
    scheduledTimeSlot?: string;
    requestedDate?: string;
    scheduledDate?: string;
    contractor?: ContractorSummary;
    rating?: Rating;
    createdAt: string;
    photoUrl?: string;
    customerName?: string;
    jobSortOrder?: number;
    contractorPayoutAmount: number;
    totalPrice: number;
    serviceFeeContractorPayout?: number;
    hasLineItems: boolean;
    originatingEstimateId?: string;
    earlyStartStatus?: string;
    donationCenter?: { id: string; name: string; address: string; lat?: number; lng?: number };
    shredding?: { categoriesConfirmed: boolean; requestType: string; serviceLocation?: string; sizeName?: string };
}

export interface PricingRule {
    id: string; serviceType: string; serviceTypeName: string;
    basePrice: number; pricePerUnit: number; unitLabel: string; isActive: boolean;
    allowPhotoUpload: boolean;
    description?: string;
    requiredSkills?: string[];
    preorderEnabled?: boolean;
    launchDate?: string | null;
    requiresDonationCenter?: boolean;
    estimateWholePile?: boolean;
    requiresDocumentCategories?: boolean;
    shreddingRetrievalFee?: number;
    allowGifting?: boolean;
}

export interface PreorderOfferingResponse {
    pricingRuleId: string; serviceTypeName: string; unitLabel: string; price: number; description?: string;
}
export interface CreateProductPreorderRequest {
    pricingRuleId: string; contactName: string; contactEmail: string; contactPhone?: string;
    deliveryAddress: string; deliveryLat: number; deliveryLng: number;
    city?: string; state?: string; county?: string; postalCode?: string; country?: string; quantity: number;
}
export interface ServiceAreaRuleResponse {
    id: string; type: 'State' | 'County' | 'Zip' | 'Radius'; mode: 'Include' | 'Exclude';
    state?: string; county?: string; zip?: string;
    centerLat?: number; centerLng?: number; radiusMiles?: number; centerAddress?: string;
}
export interface BusinessLicenseResponse {
    id: string; state?: string; county?: string; city?: string; licenseNumber?: string;
    documentUrl: string; issuedDate?: string; expiryDate?: string;
    status: 'Pending' | 'Submitted' | 'Approved' | 'Rejected' | 'Expired';
    rejectionReason?: string; uploadedAt: string;
}
export interface ProductPreorderResponse {
    id: string; serviceTypeName: string; contactName: string; contactEmail: string; contactPhone?: string;
    deliveryAddress: string; quantity: number; unitPrice: number; totalPrice: number;
    paymentStatus: string; fulfillmentStatus: string; createdAt: string;
}
export interface CreateProductPreorderResponse {
    preorder: ProductPreorderResponse; clientSecret: string; paymentIntentId: string;
}

export interface LocationPush { jobId: string; lat: number; lng: number; headingDegrees?: number; etaMinutes?: number; distanceMiles?: number; jobStatus: string; timestamp: string; }

export interface PaymentIntentResponse {
    clientSecret: string; paymentIntentId: string; amount: number; publishableKey: string;
}

export interface CancellationPolicyResponse {
    canCancel: boolean; refundAmount: number; cancellationFee: number; policyMessage: string;
}

export interface CancellationResponse {
    jobId: string; status: string; cancellationReason: string; refundAmount: number;
    cancellationFee: number; message: string; cancelledAt: string;
}

export interface PriceAdjustmentResponse {
    jobId: string; estimatedUnits: number; actualUnits: number; originalPrice: number;
    adjustedPrice: number; difference: number; serviceFeeAmount: number; taxAmount: number;
    status: string; notes?: string; requestedAt: string;
}


// ── Dumpster Rental Types ─────────────────────────────────────────────────────

export interface DumpsterSizeResponse {
    id: string; yards: number; description: string;
    deliveryPrice: number; dailyRate: number; maxRentalDays: number; isAvailable: boolean;
}

export interface DumpsterVendorResponse {
    id: string; companyName: string; contactName: string; email: string; phoneNumber?: string;
    serviceAreaLat: number; serviceAreaLng: number; serviceAreaRadiusMiles: number;
    serviceAreaAddress?: string;
    isActive: boolean; stripeConnectOnboardingComplete: boolean;
    onboardingStatus: string;
    sizes: DumpsterSizeResponse[];
    vendorRateType: string;
    vendorRateAmount?: number;
}

export interface VendorConnectStatusResponse {
    hasAccount: boolean;
    onboardingComplete: boolean;
    accountId?: string;
    onboardingUrl?: string;
    totalEarned: number;
    pendingPayout: number;
    completedOrders: number;
}

export interface VendorPayoutHistoryItem {
    orderId: string;
    deliveryAddress: string;
    completedAt: string;
    orderRevenue: number;
    vendorPayout: number;
    payoutStatus: string;
    stripeTransferId?: string;
}

export interface VendorDocumentStatusResponse {
    businessLicenseStatus: string;
    businessLicensePhotoUrl?: string;
    businessLicenseRejectionReason?: string;
    liabilityInsuranceStatus: string;
    liabilityInsurancePhotoUrl?: string;
    liabilityInsuranceRejectionReason?: string;
    equipmentDocStatus: string;
    equipmentDocPhotoUrl?: string;
    equipmentDocRejectionReason?: string;
    onboardingStatus: string;
    canManageOrders: boolean;
}

export interface VendorDocumentItem {
    vendorId: string;
    companyName: string;
    email: string;
    businessLicenseStatus: string;
    businessLicensePhotoUrl?: string;
    businessLicenseRejectionReason?: string;
    liabilityInsuranceStatus: string;
    liabilityInsurancePhotoUrl?: string;
    liabilityInsuranceRejectionReason?: string;
    equipmentDocStatus: string;
    equipmentDocPhotoUrl?: string;
    equipmentDocRejectionReason?: string;
    onboardingStatus: string;
    createdAt: string;
}

export interface UpdateVendorRequest {
    companyName: string;
    contactName: string;
    phoneNumber?: string;
    serviceAreaAddress?: string;
    serviceAreaLat: number;
    serviceAreaLng: number;
    serviceAreaRadiusMiles: number;
}

export interface DumpsterQuoteResponse {
    vendorId: string; vendorName: string; sizeId: string; yards: number;
    deliveryPrice: number; dailyRate: number; estimatedTotal: number; rentalDays: number;
    loadingHourlyRate: number;
}

export interface BookDumpsterRequest {
    sizeId: string; deliveryAddress: string; deliveryLat: number; deliveryLng: number;
    requestedDeliveryDate: string; rentalDays: number;
    loadingRequested: boolean; loadingRunnersRequested: number;
    // Needed for an accurate sales-tax jurisdiction lookup — lat/lng alone isn't enough.
    deliveryCity?: string; deliveryState?: string; deliveryPostalCode?: string; deliveryCountry?: string;
}

export interface LoadingJobResponse {
    id: string; orderId: string; deliveryAddress: string; loadingScheduledAt?: string;
    runnersRequested: number; status: string; contractorId?: string; contractorName?: string;
    clockedInAt?: string; clockedOutAt?: string; hoursWorked?: number;
    customerApprovedHours: boolean; finalCost?: number;
}

export interface DumpsterOrderResponse {
    id: string; customerId: string; customerName: string;
    vendorId: string; vendorName: string; dumpsterYards: number;
    deliveryAddress: string; requestedDeliveryDate: string; rentalDays: number;
    deliveryPrice: number; dailyRate: number; totalRentalCost: number;
    overageCost: number; loadingCost: number; taxAmount: number; totalCost: number;
    loadingRequested: boolean; loadingRunnersRequested: number;
    status: string; rentalStartedAt?: string; rentalEndsAt?: string;
    actualDeliveryDate?: string; pickupRequestedAt?: string;
    createdAt: string; loadingJobs: LoadingJobResponse[];
}

export interface RegisterVendorRequest {
    companyName: string; contactName: string; email: string; password: string;
    phoneNumber?: string; serviceAreaLat: number; serviceAreaLng: number; serviceAreaRadiusMiles: number;
    serviceAreaAddress?: string;
}

export interface AddDumpsterSizeRequest {
    yards: number; description: string; deliveryPrice: number; dailyRate: number; maxRentalDays: number;
}

export interface UpdateDumpsterOrderRequest {
    requestedDeliveryDate?: string;
    rentalDays?: number;
    loadingRequested?: boolean;
    loadingRunnersRequested?: number;
    notes?: string;
}

// ── Document Verification Types ──────────────────────────────────────────────

export interface DocumentStatusResponse {
    driversLicenseStatus: string;
    driversLicenseExpiry?: string;
    driversLicenseRejectionReason?: string;
    insuranceStatus: string;
    insuranceExpiry?: string;
    insuranceRejectionReason?: string;
    vehicleStatus: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: number;
    vehicleColor?: string;
    vehiclePlate?: string;
    vehicleRejectionReason?: string;
    onboardingStatus: string;
    canAcceptJobs: boolean;
    daysUntilLicenseExpiry?: number;
    daysUntilInsuranceExpiry?: number;
}

export interface ContractorDocumentItem {
    contractorId: string;
    name: string;
    email: string;
    driversLicenseStatus: string;
    driversLicenseExpiry?: string;
    driversLicensePhotoUrl?: string;
    driversLicenseNumber?: string;
    driversLicenseRejectionReason?: string;
    insuranceStatus: string;
    insuranceExpiry?: string;
    insurancePhotoUrl?: string;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceRejectionReason?: string;
    vehicleStatus: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: number;
    vehicleColor?: string;
    vehiclePlate?: string;
    vehiclePhotoUrl?: string;
    vehicleRejectionReason?: string;
    onboardingStatus: string;
    createdAt: string;
}

// ── Tier / Gamification Types ────────────────────────────────────────────────

export interface ContractorTierStats {
    contractorId: string;
    name: string;
    tier: string;
    tierIcon: string;
    tierColor: string;
    jobCount: number;
    averageRating: number;
    currentStreak: number;
    longestStreak: number;
    jobsToNextTier?: number;
    ratingForNextTier?: number;
    tierProgressPercent: number;
    achievements: AchievementItem[];
    tierAchievedAt?: string;
}

export interface AchievementItem {
    key: string;
    icon: string;
    name: string;
    description: string;
    earned: boolean;
}

export interface TierDistribution {
    apprentices: number;
    journeymen: number;
    masters: number;
}

// ── Messaging Types ──────────────────────────────────────────────────────────

export interface MessageItem {
    id: string;
    jobId: string;
    senderId: string;
    senderRole: string;
    text: string;
    sentAt: string;
    isRead: boolean;
}

// ── Background Check Types ───────────────────────────────────────────────────

export interface BackgroundCheckStatusResponse {
    contractorId: string;
    contractorName: string;
    status: string;
    checkrCandidateId?: string;
    checkrReportId?: string;
    invitationUrl?: string;
    clearedAt?: string;
    expiresAt?: string;
    notes?: string;
    canAcceptJobs: boolean;
}

export interface ContractorBgCheckItem {
    contractorId: string;
    name: string;
    email: string;
    backgroundCheckStatus: string;
    checkrCandidateId?: string;
    checkrReportId?: string;
    clearedAt?: string;
    expiresAt?: string;
    createdAt: string;
    notes?: string;
}

// ── Payout Types ──────────────────────────────────────────────────────────────

export interface ConnectStatusResponse {
    hasAccount: boolean;
    onboardingComplete: boolean;
    accountId?: string;
    onboardingUrl?: string;
    totalEarned: number;
    pendingPayout: number;
    completedJobs: number;
}

export interface PayoutHistoryItem {
    jobId: string;
    serviceType: string;
    pickupAddress: string;
    completedAt: string;
    jobTotal: number;
    contractorPayout: number;
    payoutStatus: string;
    stripeTransferId?: string;
}

export interface RetryPayoutsResult {
    attempted: number;
    succeeded: number;
    failed: number;
    totalTransferred: number;
    errors: string[];
}

// ── Customer Profile & Subscription Types ─────────────────────────────────────

export interface CustomerProfile {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    notificationPrefs: { jobUpdates: boolean; promotions: boolean };
    activeSubscription?: CustomerSubscriptionDetail;
    defaultAddress?: string;
    defaultLat?: number;
    defaultLng?: number;
}

export interface AdminCustomerListItem {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    createdAt: string;
    totalJobs: number;
    activePlan?: string;
    monthlyPrice?: number;
    subscriptionStatus?: string;
    renewalDate?: string;
    pickupsUsed: number;
    pickupsRemaining?: number;
}

export interface AdminCustomerDetail {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    defaultAddress?: string;
    createdAt: string;
    totalJobs: number;
    activeSubscription?: CustomerSubscriptionDetail;
}

export interface CustomerSubscriptionDetail {
    id: string;
    planId: string;
    planName: string;
    monthlyPrice: number;
    billingInterval: string;
    includedPickups?: number;
    serviceLabel: string;
    discountPercent: number;
    priorityScheduling: boolean;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    pickupsUsed: number;
    pickupsRemaining?: number;
    willCancelAtPeriodEnd: boolean;
    cancelledAt?: string;
}

export interface SubscriptionPlan {
    id: string;
    name: string;
    description?: string;
    monthlyPrice: number;
    billingInterval: string; // Monthly | Quarterly | SemiAnnual | Annual
    includedPickups?: number;
    serviceLabel: string;    // e.g. "pickups", "visits", "tune-ups"
    discountPercent: number;
    priorityScheduling: boolean;
    isActive: boolean;
    sortOrder: number;
    stripePriceId?: string;
}

export interface CreateSubscriptionResult {
    clientSecret: string;
    subscriptionId: string;
    publishableKey: string;
}

export interface AdItem {
    id: string;
    title: string;
    subtitle?: string;
    imageData?: string;
    buttonText: string;
    buttonUrl?: string;
    isActive: boolean;
    sortOrder: number;
}

export interface QboConnectionStatus {
    isConnected: boolean;
    companyName?: string;
    realmId?: string;
    connectedAt?: string;
    tokenExpiresAt?: string;
    refreshTokenExpiresAt?: string;
    environment?: 'sandbox' | 'production';
}

export interface QboAccount {
    id: string;
    name: string;
    accountType: string;
    accountSubType: string;
}

export interface QboItem {
    id: string;
    name: string;
    description?: string;
    type: string;
}

export interface QboAccountMapping {
    id?: string;
    category: string;
    qboAccountId: string;
    qboAccountName: string;
    qboItemId?: string;
    qboItemName?: string;
    updatedAt?: string;
}

export interface QboSyncError {
    id: string;
    entityType: string;
    entityId: string;
    errorMessage: string;
    failedAt: string;
    retryCount: number;
    resolvedAt?: string;
}

// ── CRM integrations (Salesforce first; shared mapping layer built for future providers) ──

export interface SalesforceConnectionStatus {
    isConnected: boolean;
    organizationName?: string;
    instanceUrl?: string;
    connectedAt?: string;
    tokenExpiresAt?: string;
    webhookUrl?: string;
    environment?: 'sandbox' | 'production';
}

export interface CrmSyncError {
    id: string;
    provider: string;
    entityType: string;
    entityId: string;
    errorMessage: string;
    failedAt: string;
    retryCount: number;
    resolvedAt?: string;
}

export interface CrmFieldMapping {
    id: string;
    provider: string;
    entityType: string;
    targetObject: string;
    fieldMappings: Record<string, string>;
    updatedAt: string;
}

// ── Scheduling (TREK-66/67/68/69/70) ─────────────────────────────────────────

export interface AvailabilitySlot { dayOfWeek: string; startTime: string; endTime: string; }
export interface ContractorAvailabilityResponse { weeklySlots: AvailabilitySlot[]; blockedDates: string[]; }
export interface ContractorAvailabilityItem { contractorId: string; name: string; weeklySlots: AvailabilitySlot[]; blockedDates: string[]; }

export interface CalendarJob {
    id: string;
    serviceType: string;
    status: string;
    pickupAddress: string;
    requestedDate?: string;
    scheduledDate?: string;
    scheduledTimeSlot?: string;
    contractorId?: string;
    contractorName?: string;
    totalPrice: number;
    estimatedUnits: number;
    jobSortOrder?: number;
    contractorPayoutOverride?: number;
}

export interface RecurringSchedule {
    id: string;
    serviceType: string;
    pickupAddress: string;
    estimatedUnits: number;
    scheduledTimeSlot?: string;
    interval: string;
    nextOccurrenceDate: string;
    isActive: boolean;
    isPaused: boolean;
    createdAt: string;
}

export interface RouteStop { jobId: string; pickupAddress: string; suggestedOrder: number; distanceMiles?: number; }
export interface RouteOptimizationResult { stops: RouteStop[]; totalDistanceMiles: number; estimatedDriveMinutes: number; }

export interface DisclosureItem {
    id: string;
    type: string;
    title: string;
    body: string;
    version: number;
    updatedAt: string;
}

export interface LegalDocumentItem {
    id: string;
    type: string;
    title: string;
    body: string;
    updatedAt: string;
}

export interface DisclosureAcceptance {
    id: string;
    disclosureId: string;
    disclosureType: string;
    disclosureTitle: string;
    version: number;
    acceptedAt: string;
    ipAddress?: string;
    arbitrationOptOut?: boolean;
    arbitrationOptOutAt?: string;
}

// ── Tenant config / feature flag types ────────────────────────────────────────

export interface FeatureFlagsConfig {
    dumpsterRental: boolean;
    loadingJobs: boolean;
    aiEstimation: boolean;
    priceAdjustments: boolean;
    tipping: boolean;
    ratings: boolean;
    inAppChat: boolean;
    backgroundCheckRequired: boolean;
    serviceCatalogPricing: boolean;
    estimatesAndQuotes: boolean;
    partialPayments: boolean;
    contractorCertTracking: boolean;
    digitalWorkOrders: boolean;
    inventoryTracking: boolean;
    aiWarrantyLookup: boolean;
    serviceAreaMapAndLicenses: boolean;
    estimateBuilder: boolean;
    propertyRecords: boolean;
    complianceDocTracking: boolean;
    photoChecklists: boolean;
    jobSlaTracking: boolean;
    clientPortal: boolean;
    donationCenters: boolean;
    documentShredding: boolean;
}

export interface ServicesConfigDto { enabledServiceTypes: string[]; maxJobsPerDay: number | null; }
export interface PaymentConfigDto { stripeEnabled: boolean; }
export interface NotificationsConfigDto { emailEnabled: boolean; pushEnabled: boolean; smsEnabled: boolean; newCustomerAdminEmail: boolean; }
export interface JobFlowConfigDto { requirePhotoOnCompletion: boolean; autoAssignEnabled: boolean; autoConvertApprovedEstimates: boolean; }

export interface TenantConfigDto {
    features: FeatureFlagsConfig;
    services: ServicesConfigDto;
    payment: PaymentConfigDto;
    notifications: NotificationsConfigDto;
    jobFlow: JobFlowConfigDto;
}

export interface UpdateTenantConfigRequest {
    features: FeatureFlagsConfig;
    services?: ServicesConfigDto;
    payment?: PaymentConfigDto;
    notifications?: NotificationsConfigDto;
    jobFlow?: JobFlowConfigDto;
}

// ── Service catalog & flexible pricing (TREK-132) ───────────────────────────────

export type ServiceCatalogPricingType = 'Flat' | 'Hourly' | 'PartsMarkup';

export interface ServiceCatalogItemDto {
    id: string;
    name: string;
    category: string;
    description: string | null;
    pricingType: ServiceCatalogPricingType;
    price: number;
    unit: string | null;
    isActive: boolean;
    createdAt: string;
}

export interface PricingConfigDto {
    standardLaborRate: number;
    afterHoursMultiplier: number;
}

export interface ServiceFeeConfigDto {
    serviceFeeEnabled: boolean;
    serviceFeeType: 'Flat' | 'Percentage';
    serviceFeeAmount: number;
    serviceFeeContractorSplitPercent: number;
    serviceFeeLabel: string;
    salesTaxEnabled: boolean;
}

export interface JobLineItemDto {
    id: string;
    catalogItemId: string | null;
    description: string;
    pricingType: ServiceCatalogPricingType;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    createdAt: string;
}

export interface JobPricingSummaryDto {
    lineItems: JobLineItemDto[];
    total: number;
}

// ── Estimates & quote approval (TREK-78) ─────────────────────────────────────────

export type EstimateStatusType = 'Pending' | 'Approved' | 'ChangesRequested';

export interface EstimateDto {
    id: string;
    jobId: string;
    number: number;
    totalAmount: number;
    status: EstimateStatusType;
    customerNote: string | null;
    createdAt: string;
    respondedAt: string | null;
}

// ── Estimate Builder — prospect-first, typed line items, branded PDF ─────────────────
// A separate, richer system from the plain Estimate/EstimateStatus above (TREK-78's
// original HVAC pilot flow, left as-is for Fobbus AC's current usage).

export type EstimateLineType = 'Service' | 'Labor' | 'Material';
export type EstimateBuilderStatusType = 'Draft' | 'Sent' | 'Viewed' | 'Approved' | 'Declined' | 'Expired' | 'ChangesRequested' | 'Requested';

export interface ProspectDto {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    billingAddress: string | null;
    customerId: string | null;
    billingLat: number | null;
    billingLng: number | null;
    billingCity: string | null;
    billingState: string | null;
    billingPostalCode: string | null;
    billingCountry: string | null;
}

export interface EstimateLineItemDto {
    id: string;
    catalogItemId: string | null;
    inventoryItemId: string | null;
    lineType: EstimateLineType;
    description: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    unitCost: number | null;
    lineTotal: number;
    taxable: boolean;
    photoUrl: string | null;
    sortOrder: number;
    isOptional: boolean;
    tierGroup: string | null;
    isSelected: boolean;
}

export interface EstimateBuilderDocDto {
    id: string;
    jobId: string | null;
    prospect: ProspectDto;
    jobSiteAddress: string;
    jobSiteLat: number | null;
    jobSiteLng: number | null;
    jobSiteCity: string | null;
    jobSiteState: string | null;
    jobSiteCounty: string | null;
    jobSitePostalCode: string | null;
    jobSiteCountry: string | null;
    number: number;
    revisionNumber: number;
    status: EstimateBuilderStatusType;
    subtotal: number;
    discountAmount: number;
    discountLabel: string | null;
    taxAmount: number;
    totalAmount: number;
    depositAmount: number | null;
    createdAt: string;
    sentAt: string | null;
    viewedAt: string | null;
    respondedAt: string | null;
    expirationDate: string | null;
    paymentTerms: string | null;
    warrantyNotes: string | null;
    contractDisclaimerText: string | null;
    customerNote: string | null;
    signed: boolean;
    signedAt: string | null;
    signedByName: string | null;
    pdfUrl: string | null;
    convertedJobId: string | null;
    convertedAt: string | null;
    lineItems: EstimateLineItemDto[];
    requestNotes: string | null;
    requestPhotoUrls: string[];
    assignedContractorId: string | null;
    assignedContractorName: string | null;
    paymentAuthorized: boolean;
}

// Shared by the logged-in-customer and anonymous-prospect "Request an Estimate" endpoints.
export interface RequestEstimateRequest {
    name: string;
    email: string;
    phone?: string | null;
    jobSiteAddress: string;
    jobSiteLat?: number | null;
    jobSiteLng?: number | null;
    jobSiteCity?: string | null;
    jobSiteState?: string | null;
    jobSiteCounty?: string | null;
    jobSitePostalCode?: string | null;
    jobSiteCountry?: string | null;
    notes?: string | null;
    photosBase64?: string[];
}

export interface NewProspectInput {
    name: string;
    email?: string | null;
    phone?: string | null;
    billingAddress?: string | null;
    billingLat?: number | null;
    billingLng?: number | null;
    billingCity?: string | null;
    billingState?: string | null;
    billingPostalCode?: string | null;
    billingCountry?: string | null;
}

export interface CreateEstimateBuilderRequest {
    prospectId?: string | null;
    newProspect?: NewProspectInput | null;
    jobSiteAddress: string;
    jobId?: string | null;
    jobSiteLat?: number | null;
    jobSiteLng?: number | null;
    jobSiteCity?: string | null;
    jobSiteState?: string | null;
    jobSiteCounty?: string | null;
    jobSitePostalCode?: string | null;
    jobSiteCountry?: string | null;
}

export interface UpdateEstimateBuilderRequest {
    paymentTerms?: string | null;
    warrantyNotes?: string | null;
    contractDisclaimerText?: string | null;
    expirationDate?: string | null;
    depositAmount?: number | null;
    discountAmount: number;
    discountLabel?: string | null;
    taxAmount: number;
}

export interface AddEstimateLineItemRequest {
    catalogItemId?: string | null;
    inventoryItemId?: string | null;
    lineType: EstimateLineType;
    description?: string | null;
    unit?: string | null;
    quantity: number;
    unitPrice?: number | null;
    unitCost?: number | null;
    taxable?: boolean;
    photoBase64?: string | null;
    isOptional?: boolean;
    tierGroup?: string | null;
}

export interface UpdateEstimateLineItemRequest {
    lineType: EstimateLineType;
    description: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    unitCost?: number | null;
    taxable: boolean;
    photoBase64?: string | null;
    removePhoto?: boolean;
    isOptional?: boolean;
    tierGroup?: string | null;
}

export interface SendEstimateResponse {
    estimate: EstimateBuilderDocDto;
    publicLink: string;
}

// Public, tokenized view — no cost fields, no internal-only data (mirrors the backend's
// PublicEstimateDto / EstimateLineItemPublicDto split).
export interface EstimateLineItemPublicDto {
    id: string;
    lineType: EstimateLineType;
    description: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    photoUrl: string | null;
    sortOrder: number;
    isOptional: boolean;
    tierGroup: string | null;
    isSelected: boolean;
}

export interface PublicEstimateDto {
    id: string;
    prospectName: string;
    jobSiteAddress: string;
    number: number;
    status: EstimateBuilderStatusType;
    subtotal: number;
    discountAmount: number;
    discountLabel: string | null;
    taxAmount: number;
    totalAmount: number;
    depositAmount: number | null;
    createdAt: string;
    expirationDate: string | null;
    paymentTerms: string | null;
    warrantyNotes: string | null;
    contractDisclaimerText: string | null;
    signed: boolean;
    signedAt: string | null;
    pdfUrl: string | null;
    lineItems: EstimateLineItemPublicDto[];
    paymentAuthorized: boolean;
}

// ── Automated invoicing (TREK-79) ─────────────────────────────────────────────────

export interface InvoiceDto {
    id: string;
    jobId: string;
    number: number;
    totalAmount: number;
    createdAt: string;
    emailSentAt: string | null;
}

// ── Partial payments (TREK-80) ─────────────────────────────────────────────────────

export type PaymentMilestoneStatusType = 'Pending' | 'Captured';

export interface PaymentMilestoneDto {
    id: string;
    jobId: string;
    label: string;
    dueDescription: string | null;
    sequenceOrder: number;
    amount: number;
    status: PaymentMilestoneStatusType;
    capturedAt: string | null;
}

export interface PaymentScheduleDto {
    milestones: PaymentMilestoneDto[];
    totalAmount: number;
    remainingBalance: number;
}

export interface CreateMilestoneIntentResponse {
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    publishableKey: string;
}

// ── Platform admin types ───────────────────────────────────────────────────────

export interface BrandingConfig {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string | null;
    buttonColor: string | null;
    navTextColor: string | null;
    logoUrl: string | null;
    appName: string;
    faviconUrl: string | null;
    splashImageUrl: string | null;
    supportEmail: string | null;
    serviceNoun: string | null;
    serviceDescription: string | null;
    contractorNoun: string | null;
    logoLinkUrl: string | null;
    stripePublishableKey?: string | null; // server-provided, read-only — not editable via updateBranding
}

export interface BusinessInfo {
    businessLegalName: string | null;
    businessAddressLine1: string | null;
    businessAddressLine2: string | null;
    businessCity: string | null;
    businessState: string | null;
    businessPostalCode: string | null;
    businessPhone: string | null;
    taxId: string | null;
    defaultContractDisclaimerText: string | null;
}

export interface TenantDetail {
    id: string;
    name: string;
    slug: string;
    domain: string | null;
    supportEmail: string | null;
    isActive: boolean;
    createdAt: string;
    branding: BrandingConfig;
    businessInfo: BusinessInfo;
    jobCount?: number;
    contractorCount?: number;
    customerCount?: number;
}

export interface MonthlyCount {
    month: string;
    jobCount: number;
    revenue: number;
}

export interface TenantStats {
    totalJobs: number;
    totalRevenue: number;
    activeContractors: number;
    totalCustomers: number;
    jobsByMonth: MonthlyCount[];
}

export interface PlatformStats {
    totalTenants: number;
    activeTenants: number;
    totalJobsThisMonth: number;
    totalRevenueThisMonth: number;
    totalContractors: number;
    totalCustomers: number;
}

export interface ActiveSession {
    userId: string;
    name: string;
    email: string;
    role: string;
    tenantId?: string;
    tenantName?: string;
    ipAddress: string;
    firstSeenAt: string;
    lastSeenAt: string;
}

// ── Platform admin API ────────────────────────────────────────────────────────

function getPlatformToken() { return localStorage.getItem('br_platform_token'); }

async function platformReq<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getPlatformToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || res.statusText);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

export interface TenantAdminDto {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    createdAt: string;
}

// ── App Marketplace ─────────────────────────────────────────────────────────────

export type AppPricingModel = 'Free' | 'Paid';
export type AppSetupType = 'Toggle' | 'OAuthConnect' | 'ApiKeyEntry';
export type MarketplaceAppStatus = 'Draft' | 'Published' | 'Archived';
export type TenantAppInstallationStatus = 'PendingSetup' | 'Active' | 'Disabled';

export interface AppFeatureItemDto { title: string; description: string; }

export interface MarketplaceAppCatalogDto {
    id: string;
    key: string;
    name: string;
    shortDescription: string;
    logoUrl: string | null;
    category: string;
    isFeatured: boolean;
    pricingModel: AppPricingModel;
    monthlyPrice: number | null;
    setupType: AppSetupType;
}

export interface MarketplaceAppDetailDto {
    id: string;
    key: string;
    name: string;
    shortDescription: string;
    longDescriptionHtml: string;
    logoUrl: string | null;
    category: string;
    pricingModel: AppPricingModel;
    monthlyPrice: number | null;
    setupType: AppSetupType;
    setupConfigJson: string | null;
    features: AppFeatureItemDto[];
    prerequisites: string[];
    installSteps: string[];
    screenshotUrls: string[];
    developerName: string;
    supportUrl: string | null;
    supportEmail: string | null;
}

export interface PlatformIntegrationCredentialDto {
    provider: string;
    environment: 'sandbox' | 'production';
    clientId: string | null;
    maskedClientSecret: string | null;
    updatedAt: string | null;
}

export interface MarketplaceAppAdminDto extends Omit<MarketplaceAppDetailDto, 'setupConfigJson'> {
    isFeatured: boolean;
    sortOrder: number;
    stripeProductId: string | null;
    stripePriceId: string | null;
    setupConfigJson: string | null;
    status: MarketplaceAppStatus;
    createdAt: string;
    publishedAt: string | null;
}

export interface CreateMarketplaceAppRequest {
    key: string;
    name: string;
    shortDescription: string;
    longDescriptionHtml?: string | null;
    logoUrl?: string | null;
    category?: string | null;
    isFeatured: boolean;
    sortOrder: number;
    pricingModel: AppPricingModel;
    monthlyPrice?: number | null;
    stripeProductId?: string | null;
    stripePriceId?: string | null;
    setupType: AppSetupType;
    setupConfigJson?: string | null;
    features?: AppFeatureItemDto[];
    prerequisites?: string[];
    installSteps?: string[];
    screenshotUrls?: string[];
    developerName?: string | null;
    supportUrl?: string | null;
    supportEmail?: string | null;
}

export type UpdateMarketplaceAppRequest = Partial<CreateMarketplaceAppRequest>;

export interface TenantAppInstallationDto {
    id: string;
    marketplaceAppId: string;
    appKey: string;
    appName: string;
    status: TenantAppInstallationStatus;
    installedAt: string;
    uninstalledAt: string | null;
    subscriptionStatus: string | null;
}

export interface InstallAppResponse { installation: TenantAppInstallationDto; }
export interface AppCheckoutResponse { checkoutUrl: string; }

// ── Tenant Templates ──────────────────────────────────────────────────────────

export type TenantTemplateStatus = 'Draft' | 'Published' | 'Archived';

export interface TemplatePricingRuleSeed { serviceTypeName: string; basePrice: number; pricePerUnit: number; unitLabel: string; }
export interface TemplateServiceCatalogItemSeed { name: string; category: string; pricingType: string; price: number; unit: string | null; }

export interface TenantTemplateDto {
    id: string;
    key: string;
    name: string;
    description: string;
    featureFlagOverrides: Record<string, boolean>;
    marketplaceAppKeys: string[];
    starterPricingRules: TemplatePricingRuleSeed[];
    starterServiceCatalogItems: TemplateServiceCatalogItemSeed[];
    serviceNoun: string | null;
    contractorNoun: string | null;
    status: TenantTemplateStatus;
    createdAt: string;
    updatedAt: string;
}

export interface CreateTenantTemplateRequest {
    key: string;
    name: string;
    description: string;
    featureFlagOverrides?: Record<string, boolean>;
    marketplaceAppKeys?: string[];
    starterPricingRules?: TemplatePricingRuleSeed[];
    starterServiceCatalogItems?: TemplateServiceCatalogItemSeed[];
    serviceNoun?: string | null;
    contractorNoun?: string | null;
}

export type UpdateTenantTemplateRequest = Partial<CreateTenantTemplateRequest>;

export interface ApplyTemplateResponse {
    flagsApplied: string[];
    freeAppsInstalled: string[];
    paidAppsSuggested: string[];
    pricingRulesCreated: number;
    pricingRulesSkippedExisting: number;
    serviceCatalogItemsCreated: number;
    serviceCatalogItemsSkippedExisting: number;
}

export const platformApi = {
    login: (email: string, password: string) =>
        platformReq<LoginResponse>('POST', '/api/platform/login', { email, password }),

    getTenants: () =>
        platformReq<TenantDetail[]>('GET', '/api/platform/tenants'),

    getTenant: (id: string) =>
        platformReq<TenantDetail>('GET', `/api/platform/tenants/${id}`),

    createTenant: (data: { name: string; slug: string; domain: string | null; supportEmail: string | null }) =>
        platformReq<TenantDetail>('POST', '/api/platform/tenants', data),

    updateBranding: (id: string, branding: Partial<BrandingConfig> & Partial<BusinessInfo> & { logoUrl?: string | null }) =>
        platformReq<TenantDetail>('PUT', `/api/platform/tenants/${id}/branding`, branding),

    uploadLogo: async (id: string, file: File): Promise<{ logoUrl: string }> => {
        const token = getPlatformToken();
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${BASE}/api/platform/tenants/${id}/logo`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error(err.error || res.statusText);
        }
        return res.json();
    },

    uploadSplash: async (id: string, file: File): Promise<{ splashImageUrl: string }> => {
        const token = getPlatformToken();
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${BASE}/api/platform/tenants/${id}/splash`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error(err.error || res.statusText);
        }
        return res.json();
    },

    setActive: (id: string, isActive: boolean) =>
        platformReq<{ id: string; isActive: boolean }>('PATCH', `/api/platform/tenants/${id}/active`, { isActive }),

    getTenantAdmins: (tenantId: string) =>
        platformReq<TenantAdminDto[]>('GET', `/api/platform/tenants/${tenantId}/admins`),

    createTenantAdmin: (tenantId: string, data: { name: string; email: string; password?: string }) =>
        platformReq<TenantAdminDto>('POST', `/api/platform/tenants/${tenantId}/admins`, data),

    getPlatformStats: () =>
        platformReq<PlatformStats>('GET', '/api/platform/stats'),

    getActiveSessions: (withinMinutes = 10) =>
        platformReq<ActiveSession[]>('GET', `/api/platform/active-sessions?withinMinutes=${withinMinutes}`),

    getTenantStats: (tenantId: string) =>
        platformReq<TenantStats>('GET', `/api/platform/tenants/${tenantId}/stats`),

    getTenantConfig: (tenantId: string) =>
        platformReq<TenantConfigDto>('GET', `/api/platform/tenants/${tenantId}/config`),

    updateTenantConfig: (tenantId: string, config: UpdateTenantConfigRequest) =>
        platformReq<TenantConfigDto>('PUT', `/api/platform/tenants/${tenantId}/config`, config),

    // ── Platform-managed OAuth app credentials (Salesforce, QuickBooks, ...) ────
    getPlatformIntegrations: () =>
        platformReq<PlatformIntegrationCredentialDto[]>('GET', '/api/platform/integrations'),
    setPlatformIntegration: (provider: string, environment: 'sandbox' | 'production', clientId: string, clientSecret: string) =>
        platformReq<void>('PUT', `/api/platform/integrations/${provider}/${environment}`, { clientId, clientSecret }),

    // ── App Marketplace catalog authoring ───────────────────────────────────────
    getMarketplaceApps: () =>
        platformReq<MarketplaceAppAdminDto[]>('GET', '/api/platform/marketplace-apps'),
    getMarketplaceApp: (id: string) =>
        platformReq<MarketplaceAppAdminDto>('GET', `/api/platform/marketplace-apps/${id}`),
    createMarketplaceApp: (data: CreateMarketplaceAppRequest) =>
        platformReq<MarketplaceAppAdminDto>('POST', '/api/platform/marketplace-apps', data),
    updateMarketplaceApp: (id: string, data: UpdateMarketplaceAppRequest) =>
        platformReq<MarketplaceAppAdminDto>('PUT', `/api/platform/marketplace-apps/${id}`, data),
    publishMarketplaceApp: (id: string) =>
        platformReq<MarketplaceAppAdminDto>('POST', `/api/platform/marketplace-apps/${id}/publish`, {}),
    archiveMarketplaceApp: (id: string) =>
        platformReq<MarketplaceAppAdminDto>('POST', `/api/platform/marketplace-apps/${id}/archive`, {}),

    uploadMarketplaceAppLogo: async (id: string, file: File): Promise<{ logoUrl: string }> => {
        const token = getPlatformToken();
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${BASE}/api/platform/marketplace-apps/${id}/logo`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error(err.error || res.statusText);
        }
        return res.json();
    },

    uploadMarketplaceAppScreenshot: async (id: string, file: File): Promise<{ screenshotUrl: string; screenshotUrls: string[] }> => {
        const token = getPlatformToken();
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${BASE}/api/platform/marketplace-apps/${id}/screenshots`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error(err.error || res.statusText);
        }
        return res.json();
    },

    // ── Tenant Templates ─────────────────────────────────────────────────────
    getTenantTemplates: () =>
        platformReq<TenantTemplateDto[]>('GET', '/api/platform/tenant-templates'),
    getTenantTemplate: (id: string) =>
        platformReq<TenantTemplateDto>('GET', `/api/platform/tenant-templates/${id}`),
    createTenantTemplate: (data: CreateTenantTemplateRequest) =>
        platformReq<TenantTemplateDto>('POST', '/api/platform/tenant-templates', data),
    updateTenantTemplate: (id: string, data: UpdateTenantTemplateRequest) =>
        platformReq<TenantTemplateDto>('PUT', `/api/platform/tenant-templates/${id}`, data),
    publishTenantTemplate: (id: string) =>
        platformReq<TenantTemplateDto>('POST', `/api/platform/tenant-templates/${id}/publish`, {}),
    archiveTenantTemplate: (id: string) =>
        platformReq<TenantTemplateDto>('POST', `/api/platform/tenant-templates/${id}/archive`, {}),
    applyTemplateToTenant: (tenantId: string, templateId: string) =>
        platformReq<ApplyTemplateResponse>('POST', `/api/platform/tenants/${tenantId}/apply-template/${templateId}`, {}),
};
